import Swal from 'sweetalert2'
import { getAccessToken } from '@/utils/authToken'
import { useAuthStore } from '@/stores/auth'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { h, createApp } from 'vue'
import router from '@/router'
import { sanitizeHtml } from '@/utils/fieldUtils'

export const getThemeColors = () => {
  return {
    background: '#ffffff',
    text: '#000000',
    popup: '#ffffff',
    input: '#ffffff',
    backdrop: 'rgba(0,0,0,0.4)',
    primary: 'var(--color-primary)',
    secondary: 'var(--color-secondary)',
    success: 'var(--color-success)',
    danger: 'var(--color-danger)',
    warning: 'var(--color-warning)',
    info: 'var(--color-info)',
    light: 'var(--color-light)',
    dark: 'var(--color-dark)',
  }
}

const showNotification = (title, text, icon = 'info', timer = 5000) => {
  const colors = getThemeColors()

  return Swal.fire({
    title,
    text,
    icon,
    timer,
    position: 'top-end',
    toast: true,
    showCloseButton: true,
    timerProgressBar: true,
    showConfirmButton: false,
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-notification',
      title: 'quitly-notification-title',
      content: 'quitly-notification-content',
    },
  })
}

export const showSuccess = (message, title = 'Erfolg', timer = 5000) => {
  return showNotification(title, message, 'success', timer)
}

export const showError = (message, title = 'Fehler', timer = 5000) => {
  return showNotification(title, message, 'error', timer)
}

export const showInfo = (message, title = 'Information', timer = 5000) => {
  return showNotification(title, message, 'info', timer)
}

export const showWarning = (message, title = 'Warnung', timer = 5000) => {
  return showNotification(title, message, 'warning', timer)
}

export const useNotification = () => {
  return {
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showConfirmDialog,
    showConfirmDialogWithDeny,
    showLeaveTeamModal,
    showDeleteTeamModal,
    showRejectRequestModal,
    showUserResponsesModal,
    showTaxDetailsModal,
    showRefundDataModal,
    showRefundTypeSelectionModal,
    showExcelModal,
    showXMLModal,
    showPaymentDateModal,
    showPayPalNameModal,
    showPayPalWizardModal,
    showPricingInfoModal,
    showSellModal,
  }
}

export const showConfirmDialog = (
  title,
  text,
  confirmButtonText = 'Bestätigen',
  cancelButtonText = 'Abbrechen',
  options = {},
) => {
  const colors = getThemeColors()

  return Swal.fire({
    title,
    text,
    icon: options?.icon || 'question',
    iconColor: options?.iconColor || undefined,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
    background: colors.background,
    color: colors.text,
    confirmButtonColor: options?.confirmButtonColor || colors.primary,
    cancelButtonColor: options?.cancelButtonColor || colors.secondary,
    denyButtonColor: options?.denyButtonColor || colors.danger,
    customClass: {
      popup: 'quitly-dialog',
      title: 'quitly-dialog-title',
      content: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
      cancelButton: 'quitly-dialog-cancel',
    },
  })
}

export const showConfirmDialogWithDeny = (
  title,
  text,
  confirmButtonText = 'Bestätigen',
  denyButtonText = 'Verwerfen',
  cancelButtonText = 'Abbrechen',
  options = {},
) => {
  const colors = getThemeColors()

  return Swal.fire({
    title,
    text,
    icon: options?.icon || 'question',
    iconColor: options?.iconColor || undefined,
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText,
    denyButtonText,
    cancelButtonText,
    background: colors.background,
    color: colors.text,
    confirmButtonColor: options?.confirmButtonColor || colors.primary,
    denyButtonColor: options?.denyButtonColor || colors.danger,
    cancelButtonColor: options?.cancelButtonColor || colors.secondary,
    customClass: {
      popup: 'quitly-dialog',
      title: 'quitly-dialog-title',
      content: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
      denyButton: 'quitly-dialog-deny',
      cancelButton: 'quitly-dialog-cancel',
    },
  })
}

export const showFieldInputModal = async ({
  title,
  field,
  currentValue = '',
  onConfirm,
  onDelete,
  onCancel,
}) => {
  const getFieldInput = (field, value) => {
    switch (field.type) {
      case 'text':
      case 'email':
        return `<input type="${field.type}" class="form-control" id="field-${field.id}" value="${value}" placeholder="${field.placeholder || ''}" />`
      case 'number':
        return `<input type="number" class="form-control" id="field-${field.id}" value="${value}" placeholder="${field.placeholder || ''}" />`
      case 'date':
        let dateValue = currentValue
        if (currentValue) {
          try {
            if (/^\d{4}-\d{2}-\d{2}$/.test(currentValue)) {
              dateValue = currentValue
            } else {
              const date = new Date(currentValue)
              if (!isNaN(date.getTime())) {
                dateValue = date.toISOString().split('T')[0]
              }
            }
          } catch (e) {
            dateValue = currentValue
          }
        }
        return `<input type="date" class="form-control" id="field-${field.id}" value="${dateValue}" placeholder="${field.placeholder || ''}" />`
      case 'textarea':
        return `<textarea class="form-control" id="field-${field.id}" rows="3" placeholder="${field.placeholder || ''}">${value}</textarea>`
      case 'checkbox':
      case 'radio': {
        const labelText = field.description || field.name || ''

        const safeLabel = sanitizeHtml(labelText, {
          allowedTags: ['a'],
          allowedAttributes: {
            a: ['href', 'target', 'rel'],
          },
        })

        let isChecked = false
        if (typeof currentValue === 'boolean') {
          isChecked = currentValue
        } else if (typeof currentValue === 'string') {
          const lowerValue = currentValue.toLowerCase().trim()
          isChecked = ['true', 'ja', 'yes', '1', 'on', 'checked'].includes(lowerValue)
        } else if (typeof currentValue === 'number') {
          isChecked = currentValue !== 0
        } else {
          isChecked = Boolean(currentValue)
        }

        return `
        <div class="form-check form-switch">
          <input type="${field.type}" class="form-check-input" id="field-${field.id}" ${isChecked ? 'checked' : ''} />
          <label class="form-check-label" for="field-${field.id}">
            ${safeLabel} ${field.required ? '<span class="text-danger">*</span>' : ''}
          </label>
        </div>
      `
      }
      case 'select':
        const options = Array.isArray(field.options)
          ? field.options
          : field.options.split(',').map((opt) => opt.trim())
        return `<select class="form-select" id="field-${field.id}">${options.map((opt) => `<option value="${opt}" ${opt === value ? 'selected' : ''}>${opt}</option>`).join('')}</select>`
      default:
        return `<input type="text" class="form-control" id="field-${field.id}" value="${value}" />`
    }
  }

  const result = await Swal.fire({
    title,
    html: `
      <div class="mb-3">
        <label class="form-label">${field.name}</label>
        ${getFieldInput(field, currentValue)}
        ${field.description && field.type !== 'checkbox' && field.type !== 'radio' ? `<small class="text-muted mt-2 d-block">${sanitizeHtml(field.description)}</small>` : ''}
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Speichern',
    cancelButtonText: 'Abbrechen',
    showDenyButton: true,
    denyButtonText: field.required ? 'Pflichtfeld' : 'Löschen',
    reverseButtons: true,
    customClass: {
      container: 'field-input-modal',
      popup: 'field-input-modal-popup',
      title: 'field-input-modal-title',
      htmlContainer: 'field-input-modal-content',
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary',
      denyButton: 'btn btn-danger',
    },
    preConfirm: () => {
      const input = document.querySelector(`#field-${field.id}`)
      const value = input.type === 'checkbox' ? input.checked : input.value

      if (!value && field.required) {
        Swal.showValidationMessage('Dieses Feld ist erforderlich')
        return false
      }
      return value
    },
    didOpen: (popup) => {
      const denyButton = popup.querySelector('.swal2-deny')
      if (field.required && denyButton) {
        denyButton.disabled = true
        denyButton.style.cursor = 'not-allowed'
      }
    },
  })

  if (result.isConfirmed) {
    const value = result.value
    if (onConfirm) {
      await onConfirm(value)
    }
    return { action: 'confirm', value }
  } else if (result.isDenied) {
    if (field.required) {
      showError('Pflichtfelder können nicht gelöscht werden.')
      return { action: 'noop' }
    }
    if (onDelete) {
      await onDelete()
    }
    return { action: 'delete' }
  } else {
    if (onCancel) {
      await onCancel()
    }
    return { action: 'cancel' }
  }
}

export const showRequiredFieldsModal = async (fields, onComplete, onCancel) => {
  const steps = fields.map((field) => ({
    title: field.name,
    html: `
      <div class="mb-3">
        <label class="form-label">${field.name}</label>
        ${getFieldInput(field)}
        ${field.description ? `<small class="text-muted mt-2 d-block">${sanitizeHtml(field.description)}</small>` : ''}
      </div>
    `,
    preConfirm: () => {
      const input = document.querySelector(`#field-${field.id}`)
      if (!input) {
        console.error(`Field input not found: #field-${field.id}`)
        Swal.showValidationMessage('Eingabefeld nicht gefunden')
        return false
      }
      
      let value
      if (input.type === 'checkbox' || input.type === 'radio') {
        value = input.checked
      } else {
        value = input.value
      }

      
      const isEmpty = value === null || value === undefined || 
                     (typeof value === 'string' && value.trim() === '') ||
                     (typeof value === 'boolean' && !value)

      if (isEmpty && field.required) {
        Swal.showValidationMessage(`${field.name} ist erforderlich`)
        return false
      }
      return value
    },
  }))
  const Queue = Swal.mixin({
    progressSteps: ['?', ...steps.map((_, index) => index + 1)],
    confirmButtonText: 'Weiter &rarr;',
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    reverseButtons: true,
    allowOutsideClick: false,
    allowEscapeKey: false,
    stopKeydownPropagation: false,
    showClass: { backdrop: 'swal2-noanimation' },
    hideClass: { backdrop: 'swal2-noanimation' },
    customClass: {
      container: 'required-fields-modal',
      popup: 'required-fields-modal-popup',
      title: 'required-fields-modal-title',
      htmlContainer: 'required-fields-modal-content',
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary',
    },
  })

  const introResult = await Queue.fire({
    title: 'Erforderliche Angaben',
    html: `
      <div class="mb-3">
        <p>Es fehlen benötigte Angaben. Bitte fülle die folgenden Felder aus:</p>
        <ul class="text-start">
          ${fields.map((field) => `<li>${field.name}</li>`).join('')}
        </ul>
      </div>
    `,
    currentProgressStep: 0,
    confirmButtonText: 'Jetzt ausfüllen',
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
  })

  if (introResult.isDismissed) {
    if (onCancel) {
      await onCancel()
    }
    return { action: 'cancel' }
  }

  const results = []
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const result = await Queue.fire({
      ...step,
      currentProgressStep: i + 1,
      confirmButtonText: i === steps.length - 1 ? 'Fertig' : 'Weiter &rarr;',
    })

    if (result.isDismissed) {
      if (onCancel) {
        await onCancel()
      }
      return { action: 'cancel' }
    }

    results.push(result.value)
  }

  if (onComplete) {
    await onComplete(results)
  }
  return { action: 'complete', values: results }
}

export const showJoinTeamModal = async (fields, onComplete, onCancel) => {
  if (!fields || fields.length === 0) {
    if (onComplete) {
      await onComplete({})
    }
    return { action: 'complete', values: {} }
  }

  const requiredFields = fields.filter((field) => field.required)
  const optionalFields = fields.filter((field) => !field.required)
  const sortedFields = [...requiredFields, ...optionalFields]

  const steps = sortedFields.map((field) => ({
    title: `${field.name} ${field.required ? '(Erforderlich)' : '(Optional)'}`,
    html: `
      <div class="mb-3">
        <label class="form-label">${field.name}</label>
        ${field.description ? `<div class="form-text mb-2">${sanitizeHtml(field.description)}</div>` : ''}
        ${getFieldInput(field)}
      </div>
    `,
    preConfirm: () => {
      const input = document.querySelector(`#field-${field.id}`)
      const value = input.type === 'checkbox' ? input.checked : input.value

      if (field.required && (!value || (typeof value === 'string' && !value.trim()))) {
        Swal.showValidationMessage(`${field.name} ist erforderlich`)
        return false
      }
      return value
    },
  }))

  const Queue = Swal.mixin({
    progressSteps: steps.map((_, index) => index + 1),
    confirmButtonText: 'Weiter &rarr;',
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    reverseButtons: true,
    allowOutsideClick: false,
    allowEscapeKey: false,
    stopKeydownPropagation: false,
    showClass: { backdrop: 'swal2-noanimation' },
    hideClass: { backdrop: 'swal2-noanimation' },
    customClass: {
      container: 'join-team-modal',
      popup: 'join-team-modal-popup',
      title: 'join-team-modal-title',
      htmlContainer: 'join-team-modal-content',
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary',
    },
  })

  const responses = {}
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const field = sortedFields[i]

    const result = await Queue.fire({
      ...step,
      currentProgressStep: i + 1,
      confirmButtonText: i === steps.length - 1 ? 'Beitrittsanfrage senden' : 'Weiter &rarr;',
    })

    if (result.isDismissed) {
      if (onCancel) {
        await onCancel()
      }
      return { action: 'cancel' }
    }

    responses[field.id] = result.value
  }

  if (onComplete) {
    await onComplete(responses)
  }
  return { action: 'complete', values: responses }
}

export const showRejectRequestModal = async (userName, onConfirm, onCancel) => {
  const colors = getThemeColors()

  const result = await Swal.fire({
    title: 'Beitrittsanfrage ablehnen',
    html: `
      <div class="mb-3">
        <p>Möchtest du die Beitrittsanfrage von <strong>${userName}</strong> wirklich ablehnen?</p>
        <div class="mt-3">
          <label for="rejection-reason" class="form-label">Grund der Ablehnung (optional)</label>
          <textarea 
            id="rejection-reason" 
            class="form-control" 
            rows="3" 
            placeholder="Gib einen Grund für die Ablehnung an..."
          ></textarea>
        </div>
      </div>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Ablehnen',
    cancelButtonText: 'Abbrechen',
    confirmButtonColor: colors.danger,
    cancelButtonColor: colors.secondary,
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-dialog',
      title: 'quitly-dialog-title',
      content: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
      cancelButton: 'quitly-dialog-cancel',
    },
    preConfirm: () => {
      const textarea = document.getElementById('rejection-reason')
      return textarea.value.trim() || null
    },
  })

  if (result.isConfirmed) {
    if (onConfirm) {
      await onConfirm(result.value)
    }
    return { action: 'confirm', reason: result.value }
  } else {
    if (onCancel) {
      await onCancel()
    }
    return { action: 'cancel' }
  }
}

export const showRejectionReasonModal = async (userName, reason, rejectedAt) => {
  const colors = getThemeColors()

  return Swal.fire({
    title: 'Ablehnungsgrund',
    html: `
      <div class="mb-3">
        <p>Die Beitrittsanfrage von <strong>${userName}</strong> wurde abgelehnt.</p>
        ${rejectedAt ? `<p class="text-muted small">Abgelehnt am: ${rejectedAt}</p>` : ''}
        <div class="mt-3">
          <label class="form-label"><strong>Grund der Ablehnung:</strong></label>
          <div class="alert alert-gradient-warning mt-2">
            ${reason || 'Kein spezifischer Grund angegeben.'}
          </div>
        </div>
      </div>
    `,
    icon: 'info',
    confirmButtonText: 'Verstanden',
    background: colors.background,
    color: colors.text,
    confirmButtonColor: colors.primary,
    customClass: {
      popup: 'quitly-dialog',
      title: 'quitly-dialog-title',
      content: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
    },
  })
}




const getFieldInput = (field, currentValue = '') => {
  switch (field.type) {
    case 'text':
    case 'email':
      return `<input type="${field.type}" class="form-control" id="field-${field.id}" value="${currentValue}" placeholder="${field.placeholder || ''}" />`
    case 'number':
      return `<input type="number" class="form-control" id="field-${field.id}" value="${currentValue}" placeholder="${field.placeholder || ''}" />`
    case 'date':
      let dateValue = currentValue
      if (currentValue) {
        try {
          if (/^\d{4}-\d{2}-\d{2}$/.test(currentValue)) {
            dateValue = currentValue
          } else {
            const date = new Date(currentValue)
            if (!isNaN(date.getTime())) {
              dateValue = date.toISOString().split('T')[0]
            }
          }
        } catch (e) {
          dateValue = currentValue
        }
      }
      return `<input type="date" class="form-control" id="field-${field.id}" value="${dateValue}" placeholder="${field.placeholder || ''}" />`
    case 'textarea':
      return `<textarea class="form-control" id="field-${field.id}" rows="3" placeholder="${field.placeholder || ''}">${currentValue}</textarea>`
    case 'checkbox':
    case 'radio': {
      const labelText = field.description || field.name || ''

      const safeLabel = sanitizeHtml(labelText, {
        allowedTags: ['a'],
        allowedAttributes: {
          a: ['href', 'target', 'rel'],
        },
      })

      let isChecked = false
      if (typeof currentValue === 'boolean') {
        isChecked = currentValue
      } else if (typeof currentValue === 'string') {
        const lowerValue = currentValue.toLowerCase().trim()
        isChecked = ['true', 'ja', 'yes', '1', 'on', 'checked'].includes(lowerValue)
      } else if (typeof currentValue === 'number') {
        isChecked = currentValue !== 0
      } else {
        isChecked = Boolean(currentValue)
      }

      return `
        <div class="form-check form-switch">
          <input type="${field.type}" class="form-check-input" id="field-${field.id}" ${isChecked ? 'checked' : ''} />
          <label class="form-check-label" for="field-${field.id}">
            ${safeLabel} ${field.required ? '<span class="text-danger">*</span>' : ''}
          </label>
        </div>
      `
    }
    case 'select':
      const options = Array.isArray(field.options)
        ? field.options
        : field.options.split(',').map((opt) => opt.trim())
      return `<select class="form-select" id="field-${field.id}">${options.map((opt) => `<option value="${opt}" ${opt === currentValue ? 'selected' : ''}>${opt}</option>`).join('')}</select>`
    default:
      return `<input type="text" class="form-control" id="field-${field.id}" value="${currentValue}" />`
  }
}

export const showLeaveTeamModal = async (teamName) => {
  const colors = getThemeColors()

  return Swal.fire({
    title: 'Bereich verlassen',
    text: `Möchtest du den Bereich "${teamName}" wirklich verlassen? Es wird eine erneute Einladung von einem Bereich-Administrator benötigt, um dem Bereich erneut beizutreten. Solltest du der letzte Administrator sein, wird der Bereich gelöscht.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: colors.danger,
    cancelButtonColor: colors.secondary,
    confirmButtonText: 'Verlassen',
    cancelButtonText: 'Abbrechen',
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-dialog',
      title: 'quitly-dialog-title',
      content: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
      cancelButton: 'quitly-dialog-cancel',
    },
  })
}

export const showDeleteTeamModal = async (teamName) => {
  const colors = getThemeColors()

  return Swal.fire({
    title: 'Bereich löschen',
    text: `Möchtest du den Bereich "${teamName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: colors.danger,
    cancelButtonColor: colors.secondary,
    confirmButtonText: 'Löschen',
    cancelButtonText: 'Abbrechen',
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-dialog',
      title: 'quitly-dialog-title',
      content: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
      cancelButton: 'quitly-dialog-cancel',
    },
  })
}

export const showUserResponsesModal = async (userName, fields, responses) => {
  const colors = getThemeColors()

  const getFieldTypeLabel = (type) => {
    const labels = {
      text: 'Text',
      number: 'Zahl',
      email: 'E-Mail',
      tel: 'Telefon',
      date: 'Datum',
      checkbox: 'Checkbox',
      select: 'Auswahl',
      textarea: 'Textbereich',
    }
    return labels[type] || type
  }

  const formatFieldResponse = (field, response) => {
    if (!response && response !== 0 && response !== false) {
      return '<em class="text-muted text-dark">Keine Antwort</em>'
    }

    switch (field.type) {
      case 'checkbox':
        return response === true || response === 'true'
          ? '<span class="badge bg-success">Ja</span>'
          : '<span class="badge bg-secondary">Nein</span>'
      case 'select':
        return `<strong class="text-dark">${response}</strong>`
      case 'date':
        try {
          return `<strong>${new Date(response).toLocaleDateString('de-DE')}</strong>`
        } catch (e) {
          return `<strong>${response}</strong>`
        }
      case 'textarea':
        return `<div class="quitly-pre-wrap text-dark">${response}</div>`
      default:
        return `<strong class="text-dark">${response}</strong>`
    }
  }

  let htmlContent = `
    <div class="user-responses-modal">
      <h5>Antworten von ${userName}</h5>
      <hr>
  `

  if (fields.length === 0) {
    htmlContent += '<p class="text-muted">Keine Felder für diesen Bereich definiert.</p>'
  } else {
    fields.forEach((field) => {
      const response = responses[field.id]
      const hasResponse = response !== null && response !== undefined && response !== ''

      htmlContent += `
        <div class="mb-3">
          <div class="d-flex justify-content-between align-items-start">
            <strong>${field.name}</strong>
            ${field.required ? '<span class="badge bg-danger ms-2">Pflichtfeld</span>' : '<span class="badge bg-secondary ms-2">Optional</span>'}
          </div>
          <div class="mt-1">
            <small class="text-muted text-dark">${getFieldTypeLabel(field.type)}</small>
          </div>
          <div class="mt-2 p-2 bg-light border rounded text-dark">
            ${hasResponse ? formatFieldResponse(field, response) : '<em class="text-muted text-dark">Keine Antwort</em>'}
            </div>
        </div>
      `
    })
  }

  htmlContent += '</div>'

  return Swal.fire({
    title: 'Nutzerantworten',
    html: htmlContent,
    width: '600px',
    showCloseButton: true,
    showConfirmButton: false,
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-dialog user-responses-popup',
    },
  })
}

export const showTaxDetailsModal = async (rate) => {
  const details = {
    0: {
      title: 'Ohne Mehrwertsteuer (0%)',
      explanation:
        'Diese Option gilt, wenn du als Kleinunternehmer (§ 19 UStG) tätig bist oder eine steuerfreie ehrenamtliche Tätigkeit ausübst.',
      applications: [
        'Pauschale Aufwandsentschädigung für Schiedsrichtertätigkeit',
        'Ehrenamtliche Tätigkeit für gemeinnützigen Verein',
        'Gesamtumsätze unter 22.000 € (Kleinunternehmerregelung)',
        'Steuerfreie Regelung (z. B. § 3 Nr. 26 EStG - Übungsleiterpauschale)',
      ],
    },
    7: {
      title: 'Ermäßigter Mehrwertsteuersatz (7%)',
      explanation:
        'Dieser Satz gilt für bestimmte kulturelle, gemeinnützige oder sportbezogene Leistungen.',
      applications: [
        'Freiberuflicher Schiedsrichter mit USt-Pflicht',
        'Sportdienstleister für Turniere/Veranstaltungen',
        'Arbeit für Organisation/Eventagentur bei Sportveranstaltungen',
        'Eintritt zu gemeinnütziger Veranstaltung mit Schiedsrichterleistung',
      ],
    },
    19: {
      title: 'Regulärer Mehrwertsteuersatz (19%)',
      explanation: 'Standardsteuersatz für alle gewerblichen oder freiberuflichen Leistungen.',
      applications: [
        'Gewerblicher Schiedsrichterdienstleister oder Sportanbieter',
        'Regelmäßige Rechnungen an private Unternehmen oder Vereine',
        'Überschreitung der Kleinunternehmergrenze (über 22.000 € Umsatz p. a.)',
      ],
    },
  }

  const detail = details[rate]
  const colors = getThemeColors()

  return Swal.fire({
    title: detail.title,
    html: `
      <div class="text-start">
        <p><strong>Erläuterung:</strong><br>${detail.explanation}</p>
        <p><strong>Typische Anwendung:</strong></p>
        <ul class="text-start">
          ${detail.applications.map((app) => `<li>${app}</li>`).join('')}
        </ul>
      </div>
    `,
    icon: 'info',
    confirmButtonText: 'Verstanden',
    width: '600px',
    background: colors.background,
    color: colors.text,
    confirmButtonColor: colors.primary,
    customClass: {
      popup: 'quitly-dialog tax-details-modal',
      title: 'quitly-dialog-title',
      htmlContainer: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
    },
  })
}

export const showBillingDetailsModal = async (billing) => {
  const colors = getThemeColors()

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: '<span class="badge bg-warning">Ausstehend</span>',
      completed: '<span class="badge bg-success">Aktiv</span>',
      failed: '<span class="badge bg-danger">Fehlgeschlagen</span>',
    }
    return badges[status] || '<span class="badge bg-secondary">Unbekannt</span>'
  }

  const getTypeBadge = (type) => {
    const badges = {
      coin: '<span class="badge bg-primary">Quittung-Kauf</span>',
      team: '<span class="badge bg-info">Bereich-Upgrade</span>',
    }
    return badges[type] || '<span class="badge bg-secondary">Unbekannt</span>'
  }

  const getPaymentMethodIcon = (method) => {
    const icons = {
      paypal: '<i class="fab fa-paypal text-primary"></i>',
      stripe: '<i class="fab fa-stripe text-info"></i>',
      bank_transfer: '<i class="fas fa-university text-secondary"></i>',
    }
    return icons[method] || '<i class="fas fa-credit-card"></i>'
  }

  const getCustomerName = () => {
    if (billing.customerName) {
      return billing.customerName
    } else if (billing.User) {
      return `${billing.User.firstName} ${billing.User.lastName}`
    } else if (billing.firstName && billing.lastName) {
      return `${billing.firstName} ${billing.lastName}`
    }
    return 'Nicht verfügbar'
  }

  const getCustomerEmail = () => {
    return (
      billing.customerEmail ||
      billing.email ||
      (billing.User ? billing.User.email : 'Nicht verfügbar')
    )
  }

  return Swal.fire({
    title: `Rechnung #${billing.invoiceNumber}`,
    html: `
      <div class="billing-details">
        <div class="row g-3">
          <!-- Status und Grunddaten -->
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Allgemeine Informationen</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <strong>Typ:</strong><br>
                    ${getTypeBadge(billing.type)}
                  </div>
                  <div class="col-md-6">
                    <strong>Status:</strong><br>
                    ${getStatusBadge(billing.status)}
                  </div>
                  <div class="col-md-6 mt-2">
                    <strong>Zahlungsmethode:</strong><br>
                    ${getPaymentMethodIcon(billing.method)} ${billing.method || 'Nicht angegeben'}
                  </div>
                  <div class="col-md-6 mt-2">
                    <strong>Erstellt:</strong><br>
                    ${formatDate(billing.createdAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Kunde -->
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Kundeninformationen</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <strong>Name:</strong><br>
                    ${getCustomerName()}
                  </div>
                  <div class="col-md-6">
                    <strong>E-Mail:</strong><br>
                    ${getCustomerEmail()}
                  </div>
                  ${
                    billing.company
                      ? `
                  <div class="col-md-6 mt-2">
                    <strong>Unternehmen:</strong><br>
                    ${billing.company}
                  </div>
                  `
                      : ''
                  }
                  ${
                    billing.userId
                      ? `
                  <div class="col-md-6 mt-2">
                    <strong>Benutzer-ID:</strong><br>
                    ${billing.userId}
                  </div>
                  `
                      : ''
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Rechnungsdetails -->
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Rechnungsdetails</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  ${
                    billing.type === 'coin'
                      ? `
                  <div class="col-md-6">
                    <strong>Quittungen:</strong><br>
                    <span class="badge bg-primary">${billing.coins} Quittungen</span>
                  </div>
                  `
                      : ''
                  }
                  ${
                    billing.type === 'team'
                      ? `
                  <div class="col-md-6">
                    <strong>Plan:</strong><br>
                    <span class="badge bg-info">${billing.planName || 'Bereich-Upgrade'}</span>
                  </div>
                  `
                      : ''
                  }
                  <div class="col-md-6">
                    <strong>Nettobetrag:</strong><br>
                    ${formatCurrency(billing.amount)}
                  </div>
                  <div class="col-md-6 mt-2">
                    <strong>MwSt.:</strong><br>
                    ${formatCurrency(billing.vat)}
                  </div>
                  <div class="col-md-6 mt-2">
                    <strong>Bruttobetrag:</strong><br>
                    <strong class="text-success">${formatCurrency(billing.price || billing.amount)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Rechnungsadresse -->
          ${
            billing.street && billing.city
              ? `
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Rechnungsadresse</h6>
              </div>
              <div class="card-body">
                <address>
                  ${billing.company ? `${billing.company}<br>` : ''}
                  ${billing.firstName} ${billing.lastName}<br>
                  ${billing.street} ${billing.number || ''}<br>
                  ${billing.zip} ${billing.city}<br>
                  ${billing.country || ''}
                </address>
              </div>
            </div>
          </div>
          `
              : ''
          }

          <!-- PDF Download -->
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Dokumente</h6>
              </div>
              <div class="card-body text-center">
                <button class="btn btn-outline-primary" onclick="window.downloadBillingPDF('${billing.id}', '${billing.type}')">
                  <i class="fas fa-file-pdf me-2"></i>
                  PDF herunterladen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    width: '800px',
    showCancelButton: false,
    confirmButtonText: 'Schließen',
    background: colors.background,
    color: colors.text,
    confirmButtonColor: colors.primary,
    customClass: {
      popup: 'quitly-dialog billing-details-modal',
      title: 'quitly-dialog-title',
      content: 'quitly-dialog-content',
      confirmButton: 'quitly-dialog-confirm',
    },
    didOpen: (popup) => {
      window.downloadBillingPDF = async (billingId, billingType) => {
        try {
          const response = await fetch(`/api/admin/billings/${billingId}/pdf`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
            },
          })

          if (response.ok) {
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `Rechnung-${billing.invoiceNumber}.pdf`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
          } else {
            throw new Error('PDF konnte nicht heruntergeladen werden')
          }
        } catch (error) {
          console.error('PDF Download Fehler:', error)
          showError('PDF konnte nicht heruntergeladen werden')
        }
      }
    },
  })
}

export const showRefundTypeSelectionModal = async (
  receiptCount,
  refundMethod = 'bank_transfer',
) => {
  const colors = getThemeColors()

  let title, alertText, options

  if (refundMethod === 'paypal') {
    title = 'PayPal-Erstattung generieren'
    alertText = `${receiptCount} Quittung(en) mit PayPal-Daten gefunden`
    options = `
      <div class="row g-4">
         <div class="col-md-6">
          <div class="card h-100 border-0 shadow-sm" style="background-color: #f8f9fa;">
            <div class="card-body text-center p-4 d-flex flex-column">
              <div class="mb-3">
                ${renderToHtml(['fas', 'file-excel'], { class: 'fa-2x text-success mb-2' })}
              </div>
              <h6 class="card-title fw-semibold mb-3" style="font-family: 'Barlow', sans-serif;">Excel Liste</h6>
              <p class="card-text text-muted small mb-4 flex-grow-1">
                Excel-Datei mit PayPal-Daten
              </p>
              <button class="btn btn-success btn-sm w-100 sourGummy" onclick="selectPayPalExcel()">
                Excel generieren
              </button>
            </div>
          </div>
        </div>
        
        <div class="col-md-6">
          <div class="card h-100 border-0 shadow-sm" style="background-color: #f8f9fa;">
            <div class="card-body text-center p-4 d-flex flex-column">
              <div class="mb-3">
                ${renderToHtml(['fab', 'paypal'], { class: 'fa-2x text-primary mb-2' })}
              </div>
              <h6 class="card-title fw-semibold mb-3" style="font-family: 'Barlow', sans-serif;">PayPal Wizard</h6>
              <p class="card-text text-muted small mb-4 flex-grow-1">
                Schrittweise Erstattung pro Nutzer mit PayPal.me Links
              </p>
              <button class="btn btn-primary btn-sm w-100 sourGummy" onclick="selectPayPalWizard()" >
                Wizard starten
              </button>
            </div>
          </div>
        </div>
        
     
      </div>
    `
  } else if (refundMethod === 'cash') {
    title = 'Bar-Erstattung generieren'
    alertText = `${receiptCount} Quittung(en) mit Bar-Erstattungsdaten gefunden`
    options = `
      <div class="row g-4">
        <div class="col-md-12">
          <div class="card h-100 border-0 shadow-sm" style="background-color: #f8f9fa;">
            <div class="card-body text-center p-4 d-flex flex-column">
              <div class="mb-3">
                ${renderToHtml(['fas', 'file-excel'], { class: 'fa-2x text-success mb-2' })}
              </div>
              <h6 class="card-title fw-semibold mb-3" style="font-family: 'Barlow', sans-serif;">Bar-Erstattungsliste</h6>
              <p class="card-text text-muted small mb-4 flex-grow-1">
                Excel-Datei mit Bar-Erstattungsdaten für persönliche Auszahlung
              </p>
              <button class="btn btn-success btn-sm w-100 sourGummy" onclick="selectCashExcel()" >
               Excel generieren 
              </button>
            </div>
          </div>
        </div>
      </div>
    `
  } else if (refundMethod === 'bank_transfer') {
    title = 'Erstattungsdatei generieren'
    alertText = `${receiptCount} Quittung(en) mit IBAN-Daten gefunden`
    options = `
      <div class="row g-4">
        <div class="col-md-6">
          <div class="card h-100 border-0 shadow-sm" style="background-color: #f8f9fa;">
            <div class="card-body text-center p-4 d-flex flex-column">
              <div class="mb-3">
                ${renderToHtml(['fas', 'file-excel'], { class: 'fa-2x text-success mb-2' })}
              </div>
              <h6 class="card-title fw-semibold mb-3" style="font-family: 'Barlow', sans-serif;">Excel Liste</h6>
              <p class="card-text text-muted small mb-4 flex-grow-1">
                Excel-Datei mit IBAN-Daten
              </p>
              <button class="btn btn-success btn-sm w-100 sourGummy" onclick="selectExcel()" >
               Excel generieren 
              </button>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card h-100 border-0 shadow-sm" style="background-color: #f8f9fa;">
            <div class="card-body text-center p-4 d-flex flex-column">
              <div class="mb-3">
                ${renderToHtml(['fas', 'file-code'], { class: 'fa-2x text-primary mb-2' })}
              </div>
              <h6 class="card-title fw-semibold mb-3" style="font-family: 'Barlow', sans-serif;">XML Sammelüberweisung</h6>
              <p class="card-text text-muted small mb-4 flex-grow-1">
                XML-Datei für Banküberweisung (ZIP mit XML & Excel)
              </p>
              <button class="btn btn-primary btn-sm w-100 sourGummy" onclick="selectXML()">
                XML generieren
              </button>
            </div>
          </div>
        </div>
      </div>
    `
  }

  const result = await Swal.fire({
    title: title,
    html: `
      <div class="text-start">
        <div class="alert alert-info mb-4">
          <strong>${alertText}</strong>
        </div>
        
        ${options}
      
      </div>
    `,
    showCancelButton: false,
    confirmButtonText: 'Schließen',
    showCloseButton: true,
    allowOutsideClick: false,
    width: '600px',
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-refund-modal',
      title: 'quitly-refund-modal-title',
      htmlContainer: 'quitly-refund-modal-content',
    },
    didOpen: () => {
      window.selectExcel = () => {
        Swal.close()
        window.refundModalResult = 'excel'
      }
      window.selectXML = () => {
        Swal.close()
        window.refundModalResult = 'xml'
      }
      window.selectZIP = () => {
        Swal.close()
        window.refundModalResult = 'zip'
      }
      window.selectCashExcel = () => {
        Swal.close()
        window.refundModalResult = 'cash_excel'
      }
      window.selectPayPalWizard = () => {
        Swal.close()
        window.refundModalResult = 'paypal_wizard'
      }
      window.selectPayPalExcel = () => {
        Swal.close()
        window.refundModalResult = 'paypal_excel'
      }
    },
  })

  return window.refundModalResult || null
}

export const showExcelModal = async (receiptCount) => {
  const colors = getThemeColors()

  const result = await Swal.fire({
    title: 'Excel-Datei generieren',
    html: `
      <div class="text-center">
        <div class="alert alert-info mb-3">
          <strong>${receiptCount} Quittung(en) werden in die Excel-Datei aufgenommen</strong>
        </div>
        
        <div class="mb-3">
          <label for="paymentStatus" class="form-label">Zahlungsstatus nach Generierung:</label>
          <select id="paymentStatus" class="form-select">
            <option value="none">Nicht verändern</option>
            <option value="planed" selected>Als vorbereitet markieren</option>
            <option value="payed">Als bezahlt markieren</option>
          </select>
        </div>
        
        <div class="mb-3">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="groupByUser" checked>
            <label class="form-check-label" for="groupByUser">
              <strong>Zahlungen zusammengefasst</strong>
              <br>
              <small class="text-muted">
                Pro Nutzer wird eine zusammengefasste Buchung erstellt (z.B. "Erstattung RE-NR1, RE-NR2, RE-NR3..."). 
                Wenn deaktiviert, wird pro Quittung eine separate Buchung erstellt.
              </small>
            </label>
          </div>
        </div>
        
        <div class="alert alert-gradient-warning">
          <strong>Hinweis:</strong> Die Excel-Datei wird automatisch heruntergeladen.
        </div>
      </div>
    `,
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    confirmButtonText: 'Excel herunterladen',
    confirmButtonColor: '#198754',
    showLoaderOnConfirm: true,
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-excel-modal',
      title: 'quitly-excel-modal-title',
      htmlContainer: 'quitly-excel-modal-content',
    },
    preConfirm: () => {
      const select = document.getElementById('paymentStatus')
      const groupByUser = document.getElementById('groupByUser')
      return {
        paymentStatus: select ? select.value : 'planed',
        groupByUser: groupByUser ? groupByUser.checked : true,
      }
    },
  })

  return result.isConfirmed ? result.value : null
}

export const showXMLModal = async (receiptCount, teamId = null) => {
  const colors = getThemeColors()

  let teamSettings = null
  if (teamId) {
    try {
      const { teamService } = await import('@/api/teams')
      teamSettings = await teamService.getTeamSepaSettings(teamId)
    } catch (error) {
      console.warn('Konnte Bereich SEPA-Einstellungen nicht laden:', error)
    }
  }

  const initialVersion = teamSettings?.sepaLibVersion || 'DE-3.7'
  const initialDebtorName = teamSettings?.debtorName || ''
  const initialDebtorIBAN = teamSettings?.debtorIBAN || ''
  const initialDebtorBIC = teamSettings?.debtorBIC || ''

  const result = await Swal.fire({
    title: 'XML-Datei generieren',
    html: `
      <div class="text-center">
        <div class="alert alert-info mb-3">
          <strong>${receiptCount} Quittung(en) werden in die XML-Datei aufgenommen</strong>
        </div>
        
        <div class="row g-3">
          <div class="col-md-6">
            <label for="debtorName" class="form-label">Auftraggeber Name:</label>
            <input type="text" id="debtorName" class="form-control" value="${initialDebtorName}" placeholder="z.B. pronomiX GmbH" required>
          </div>
          <div class="col-md-6">
            <label for="debtorIBAN" class="form-label">Auftraggeber IBAN:</label>
            <div class="input-group">
              <input type="text" id="debtorIBAN" class="form-control" value="${initialDebtorIBAN}" placeholder="DE89 3704 0044 0532 0130 00" required>
              <span id="ibanValidationIcon" class="input-group-text" style="display: none;">
                <div class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Laden...</span>
                </div>
              </span>
            </div>
            <div id="ibanValidationFeedback" class="form-text" style="display: none;"></div>
          </div>
        </div>
        
        <div class="row g-3 mt-2">
          <div class="col-md-6">
            <label for="debtorBIC" class="form-label">Auftraggeber BIC:</label>
            <input type="text" id="debtorBIC" class="form-control" value="${initialDebtorBIC}" placeholder="COBADEFFXXX" required>
            <div id="bicValidationFeedback" class="form-text" style="display: none;"></div>
          </div>
          <div class="col-md-6">
            <label for="sepaLibVersion" class="form-label">SEPA-Lib Version:</label>
            <select id="sepaLibVersion" class="form-select">
              <option value="DE-2.6" ${initialVersion === 'DE-2.6' ? 'selected' : ''}>DE 2.6 (2012)</option>
              <option value="DE-2.7" ${initialVersion === 'DE-2.7' ? 'selected' : ''}>DE 2.7 (2013)</option>
              <option value="DE-2.8" ${initialVersion === 'DE-2.8' ? 'selected' : ''}>DE 2.8 (2014)</option>
              <option value="DE-2.9" ${initialVersion === 'DE-2.9' ? 'selected' : ''}>DE 2.9 (2015)</option>
              <option value="DE-3.0" ${initialVersion === 'DE-3.0' ? 'selected' : ''}>DE 3.0 (2016)</option>
              <option value="DE-3.1" ${initialVersion === 'DE-3.1' ? 'selected' : ''}>DE 3.1 (2017)</option>
              <option value="DE-3.5" ${initialVersion === 'DE-3.5' ? 'selected' : ''}>DE 3.5 (2021)</option>
              <option value="DE-3.7" ${initialVersion === 'DE-3.7' ? 'selected' : ''}>DE 3.7 (2024)</option>
            </select>
          </div>
        </div>
        
        <div class="mb-3 mt-3">
          <label for="paymentStatus" class="form-label">Zahlungsstatus nach Generierung:</label>
          <select id="paymentStatus" class="form-select">
            <option value="none">Nicht verändern</option>
            <option value="planed" selected>Als vorbereitet markieren</option>
            <option value="payed">Als bezahlt markieren</option>
          </select>
        </div>
        
        <div class="mb-3">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="groupByUser" checked>
            <label class="form-check-label" for="groupByUser">
              <strong>Zahlungen zusammengefasst</strong>
              <br>
              <small class="text-muted">
                Pro Nutzer wird eine zusammengefasste Buchung erstellt (z.B. "Erstattung RENR1, RENR2, RENR3..."). 
                Wenn deaktiviert, wird pro Quittung eine separate Buchung erstellt.
              </small>
            </label>
          </div>
        </div>
        
        <div class="alert alert-gradient-warning">
          <strong>Hinweis:</strong> Die XML-Datei wird automatisch heruntergeladen.
        </div>
      </div>
    `,
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    confirmButtonText: 'XML herunterladen',
    confirmButtonColor: '#0d6efd',
    showLoaderOnConfirm: true,
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-xml-modal',
      title: 'quitly-xml-modal-title',
      htmlContainer: 'quitly-xml-modal-content',
    },
    didOpen: (popup) => {
      const ibanInput = popup.querySelector('#debtorIBAN')
      const ibanValidationIcon = popup.querySelector('#ibanValidationIcon')
      const ibanValidationFeedback = popup.querySelector('#ibanValidationFeedback')
      const bicInput = popup.querySelector('#debtorBIC')
      const bicValidationFeedback = popup.querySelector('#bicValidationFeedback')

      let ibanValidationTimeout = null

      const validateIban = async (iban) => {
        if (!iban || iban.length < 10) {
          ibanValidationFeedback.style.display = 'none'
          ibanValidationIcon.style.display = 'none'
          ibanInput.classList.remove('is-valid', 'is-invalid')
          return
        }

        if (ibanValidationTimeout) {
          clearTimeout(ibanValidationTimeout)
        }

        ibanValidationIcon.style.display = 'inline-block'
        ibanValidationFeedback.style.display = 'none'
        ibanInput.classList.remove('is-valid', 'is-invalid')

        ibanValidationTimeout = setTimeout(async () => {
          try {
            const { ibanApi } = await import('@/api/iban')
            const result = await ibanApi.validateIban(iban)

            if (result.valid) {
              ibanInput.classList.add('is-valid')
              ibanInput.classList.remove('is-invalid')
              ibanValidationIcon.innerHTML = '✓'
              ibanValidationIcon.className = 'input-group-text text-success'

              if (result.bankName) {
                ibanValidationFeedback.innerHTML = `
                  <strong>${result.bankName}</strong>`
                ibanValidationFeedback.className = 'form-text text-success'
                ibanValidationFeedback.style.display = 'block'
              }

              if (result.bic && !bicInput.value) {
                bicInput.value = result.bic
                bicValidationFeedback.innerHTML = '✓ BIC automatisch ausgefüllt'
                bicValidationFeedback.className = 'form-text text-success'
                bicValidationFeedback.style.display = 'block'
              }
            } else {
              ibanInput.classList.add('is-invalid')
              ibanInput.classList.remove('is-valid')
              ibanValidationIcon.innerHTML = '⚠'
              ibanValidationIcon.className = 'input-group-text text-danger'
              ibanValidationFeedback.innerHTML = result.error || 'Ungültige IBAN'
              ibanValidationFeedback.className = 'form-text text-danger'
              ibanValidationFeedback.style.display = 'block'
            }
          } catch (error) {
            console.error('IBAN-Validierungsfehler:', error)
            ibanInput.classList.add('is-invalid')
            ibanInput.classList.remove('is-valid')
            ibanValidationIcon.innerHTML = '⚠'
            ibanValidationIcon.className = 'input-group-text text-danger'
            ibanValidationFeedback.innerHTML = 'Fehler bei der IBAN-Validierung'
            ibanValidationFeedback.className = 'form-text text-danger'
            ibanValidationFeedback.style.display = 'block'
          } finally {
            ibanValidationIcon.style.display = 'none'
          }
        }, 500)
      }

      ibanInput.addEventListener('input', (e) => {
        validateIban(e.target.value)
      })

      if (initialDebtorIBAN) {
        validateIban(initialDebtorIBAN)
      }
    },
    preConfirm: async () => {
      const debtorName = document.getElementById('debtorName')?.value?.trim()
      const debtorIBAN = document.getElementById('debtorIBAN')?.value?.trim()
      const debtorBIC = document.getElementById('debtorBIC')?.value?.trim()
      const sepaLibVersion = document.getElementById('sepaLibVersion')?.value || 'DE-3.7'
      const paymentStatus = document.getElementById('paymentStatus')?.value || 'planed'

      if (!debtorName) {
        Swal.showValidationMessage('Auftraggeber Name ist erforderlich')
        return false
      }

      if (!debtorIBAN) {
        Swal.showValidationMessage('Auftraggeber IBAN ist erforderlich')
        return false
      }

      if (!debtorBIC) {
        Swal.showValidationMessage('Auftraggeber BIC ist erforderlich')
        return false
      }

      try {
        const { ibanApi } = await import('@/api/iban')
        const validationResult = await ibanApi.validateIban(debtorIBAN)

        if (!validationResult.valid) {
          Swal.showValidationMessage(validationResult.error || 'Ungültige IBAN')
          return false
        }
      } catch (error) {
        console.error('IBAN-Validierungsfehler beim Speichern:', error)
      }

      const groupByUser = document.getElementById('groupByUser')

      return {
        debtorName,
        debtorIBAN: debtorIBAN.toUpperCase().replace(/\s/g, ''),
        debtorBIC: debtorBIC.toUpperCase().replace(/\s/g, ''),
        sepaLibVersion,
        paymentStatus,
        groupByUser: groupByUser ? groupByUser.checked : true,
      }
    },
  })

  if (result.isConfirmed) {
    if (teamId) {
      try {
        const { teamService } = await import('@/api/teams')
        await teamService.saveTeamSepaSettings(teamId, {
          debtorName: result.value.debtorName,
          debtorIBAN: result.value.debtorIBAN,
          debtorBIC: result.value.debtorBIC,
          sepaLibVersion: result.value.sepaLibVersion,
        })
      } catch (error) {
        console.warn('Konnte Bereich SEPA-Einstellungen nicht speichern:', error)
      }
    }

    return result.value
  }

  return null
}

export const showPaymentDateModal = async () => {
  const colors = getThemeColors()

  const result = await Swal.fire({
    title: 'Zahlungsdatum eingeben',
    html: `
      <div class="mb-3">
        <label for="paymentDate" class="form-label">Zahlungsdatum:</label>
        <input type="date" id="paymentDate" class="form-control" value="${new Date().toISOString().split('T')[0]}" />
      </div>
    `,
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    confirmButtonText: 'Als bezahlt markieren',
    confirmButtonColor: '#198754',
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-payment-modal',
      title: 'quitly-payment-modal-title',
      htmlContainer: 'quitly-payment-modal-content',
    },
    preConfirm: () => {
      const dateInput = document.getElementById('paymentDate')
      return dateInput ? dateInput.value : null
    },
  })

  return result.isConfirmed ? result.value : null
}

export const showPayPalNameModal = async () => {
  const colors = getThemeColors()

  const result = await Swal.fire({
    title: 'PayPal.me Name eingeben',
    input: 'text',
    inputLabel: 'Ihr PayPal.me Name',
    inputPlaceholder: 'z.B. maxmustermann',
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    confirmButtonText: 'Weiter',
    background: colors.background,
    color: colors.text,
    customClass: {
      popup: 'quitly-paypal-modal',
      title: 'quitly-paypal-modal-title',
      input: 'quitly-paypal-modal-input',
    },
    inputValidator: (value) => {
      if (!value) {
        return 'Bitte gib deinen PayPal.me Namen ein'
      }
      if (value.includes(' ')) {
        return 'PayPal.me Namen dürfen keine Leerzeichen enthalten'
      }
    },
  })

  return result.isConfirmed ? result.value : null
}

export const showPricingInfoModal = async (pricingRules) => {
  const colors = getThemeColors()

  const pricingInfo = pricingRules.map((rule) => {
    const pricePerCoin = parseFloat(rule.pricePerCoin) || 0
    let bruttoPricePerCoin

    if (rule.priceType === 'netto') {
      const vatAmount = pricePerCoin * 0.19
      bruttoPricePerCoin = pricePerCoin + vatAmount
    } else {
      bruttoPricePerCoin = pricePerCoin
    }

    return {
      minCoins: rule.minCoins || 'unbegrenzt',
      maxCoins: rule.maxCoins || 'unbegrenzt',
      pricePerCoin: Math.round(bruttoPricePerCoin * 1000) / 1000,
      description: rule.description || 'Standard-Preis',
    }
  })

  if (pricingInfo.length === 0) {
    pricingInfo.push({
      minCoins: 'alle',
      maxCoins: 'alle',
      pricePerCoin: 0.142,
      description: 'Standard-Preis',
    })
  }

  const result = await Swal.fire({
    title: 'Quittung-Preisübersicht',
    html: `
      <div class="pricing-info">
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Quittung-Bereich</th>
                <th>Preis pro Quittung*</th>
              </tr>
            </thead>
            <tbody>
              ${pricingInfo
                .map(
                  (info) => `
                <tr>
                  <td>
                   ${info.maxCoins === 'unbegrenzt' ? 'ab ' : ``}
                    ${info.minCoins === 'unbegrenzt' ? '1+' : info.minCoins} 
                    ${info.maxCoins === 'unbegrenzt' ? '' : `- ${info.maxCoins}`}
                  </td>
                  <td><strong>${(info.pricePerCoin || 0).toFixed(2)} €*</strong></td>
                </tr>
              `,
                )
                .join('')}
            </tbody>
          </table>
        </div>
        <div class="mt-3 small text-muted">
          * Alle Preise inklusive 19% Mehrwertsteuer
        </div>
      </div>
    `,
    icon: 'info',
    confirmButtonText: 'Verstanden',
    showCancelButton: false,
    background: colors.background,
    color: colors.text,
    customClass: {
      container: 'pricing-info-modal',
      popup: 'pricing-info-modal-popup',
      title: 'pricing-info-modal-title',
      htmlContainer: 'pricing-info-modal-content',
      confirmButton: 'btn btn-primary',
    },
  })

  return result
}

export const showPayPalWizardModal = async (receiptIds, onComplete, onCancel) => {
  const colors = getThemeColors()

  try {
    const { receiptsApi } = await import('@/api/receipts')
    const payload = {
      receiptIds: receiptIds,
      refundType: 'paypal_grouped',
    }

    const result = await receiptsApi.generateRefundFiles(payload)
    const groupedData = result.data || []

    if (groupedData.length === 0) {
      await Swal.fire({
        title: 'Keine Daten',
        text: 'Keine PayPal-Erstattungsdaten gefunden.',
        icon: 'warning',
        confirmButtonText: 'OK',
      })
      if (onCancel) onCancel()
      return null
    }

    const progressSteps = groupedData.map((_, index) => `${index + 1}`)

    const Queue = Swal.mixin({
      progressSteps: progressSteps,
      confirmButtonText: 'Nächster Nutzer &rarr;',
      showCancelButton: true,
      cancelButtonText: 'Abbrechen',
      reverseButtons: true,
      allowOutsideClick: false,
      allowEscapeKey: true,
      background: colors.background,
      color: colors.text,
      customClass: {
        container: 'paypal-wizard-modal',
        popup: 'paypal-wizard-modal-popup',
        title: 'paypal-wizard-modal-title',
        htmlContainer: 'paypal-wizard-modal-content',
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-secondary',
      },
    })

    let currentStep = 0
    const completedPayments = new Set()

    const showUserStep = async (userData, stepIndex) => {
      const receiptNumbers = userData.receipts.map((r) => r.receiptNumber).join(', ')
      const totalAmount = userData.receipts.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)
      const paypalUrl = `https://paypal.me/${userData.paypalMeName}/${totalAmount}`
      const copyIcon = await renderToHtml(FontAwesomeIcon, { icon: 'copy' })
      const html = `
         <div class="text-center">
           <div class="mb-4">
             <h5 class="fw-bold">${userData.userName}</h5>
             <div class="alert alert-primary">
               <strong>${formatCurrency(totalAmount)}</strong>
             </div>
           </div>
           
           <div class="mb-4">
             <label class="form-label fw-semibold">${renderToHtml(['fas', 'info-circle'])} Verwendungszweck:</label>
             <div class="input-group">
               <input 
                 type="text" 
                 class="form-control" 
                 value="Erstattung ${receiptNumbers}" 
                 readonly 
                 id="purpose-${stepIndex}"
               />
               <button 
                 class="btn btn-outline-secondary" 
                 type="button" 
                 onclick="copyPurpose(${stepIndex})"
               >
                ${renderToHtml(['fas', 'copy'])}
               </button>
             </div>
             <div id="copy-feedback-${stepIndex}" class="form-text text-success" style="display: none;">
               Verwendungszweck wurde in die Zwischenablage kopiert!
             </div>
           </div>
           
           <div class="mb-4">
             <a 
               href="${paypalUrl}" 
               target="_blank" 
               class="btn btn-primary btn-lg sourGummy"
             >
               ${renderToHtml(['fab', 'paypal'])} Mit PayPal.me bezahlen
             </a>
           </div>
           
           <div class="d-flex justify-content-center gap-2">
             <button 
               class="btn btn-success sourGummy" 
               onclick="markAsPaid(${stepIndex})"
             >
               ${renderToHtml(['fas', 'check'])} Als bezahlt markieren
             </button>
             <button 
               class="btn btn-warning sourGummy" 
               onclick="markAsPlanned(${stepIndex})"
             >
               ${renderToHtml(['fas', 'calendar'])} Als vorbereitet markieren
             </button>
             <button 
               class="btn btn-secondary sourGummy" 
               onclick="markAsOpen(${stepIndex})"
             >    
               ${renderToHtml(['fas', 'clock'])} Als offen markieren
             </button>
           </div>
         </div>
       `

      const stepResult = await Queue.fire({
        title: `Nutzer ${stepIndex + 1} von ${groupedData.length}`,
        html: html,
        currentProgressStep: stepIndex,
        confirmButtonText:
          stepIndex === groupedData.length - 1 ? 'Fertig' : 'Nächster Nutzer &rarr;',
        didOpen: () => {
          window.copyPurpose = (stepIndex) => {
            const input = document.getElementById(`purpose-${stepIndex}`)
            const feedback = document.getElementById(`copy-feedback-${stepIndex}`)

            navigator.clipboard.writeText(input.value).then(() => {
              feedback.style.display = 'block'
              setTimeout(() => {
                feedback.style.display = 'none'
              }, 2000)
            })
          }

          window.markAsPaid = async (stepIndex) => {
            const userData = groupedData[stepIndex]
            const receiptIds = userData.receipts.map((r) => r.id)

            try {
              const { receiptsApi } = await import('@/api/receipts')
              await receiptsApi.updateReceiptPaymentStatus(
                receiptIds,
                'payed',
                new Date().toISOString().split('T')[0],
              )
              completedPayments.add(stepIndex)

              await Swal.fire({
                title: `${renderToHtml(['fas', 'check-circle'])} Erfolgreich markiert`,
                text: 'Quittungen wurden als bezahlt markiert.',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
              })
            } catch (error) {
              console.error('Fehler beim Markieren als bezahlt:', error)
              await Swal.fire({
                title: `${renderToHtml(['fas', 'exclamation-triangle'])} Fehler`,
                text: 'Fehler beim Markieren als bezahlt.',
                icon: 'error',
                confirmButtonText: 'OK',
              })
            }
          }

          window.markAsPlanned = async (stepIndex) => {
            const userData = groupedData[stepIndex]
            const receiptIds = userData.receipts.map((r) => r.id)

            try {
              const { receiptsApi } = await import('@/api/receipts')
              await receiptsApi.updateReceiptPaymentStatus(receiptIds, 'planed')
              completedPayments.add(stepIndex)

              await Swal.fire({
                title: 'Erfolgreich markiert',
                text: 'Quittungen wurden als vorbereitet markiert.',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
              })
            } catch (error) {
              console.error('Fehler beim Markieren als vorbereitet:', error)
              await Swal.fire({
                title: `${renderToHtml(['fas', 'exclamation-triangle'])} Fehler`,
                text: 'Fehler beim Markieren als vorbereitet.',
                icon: 'error',
                confirmButtonText: 'OK',
              })
            }
          }

          window.markAsOpen = async (stepIndex) => {
            const userData = groupedData[stepIndex]
            const receiptIds = userData.receipts.map((r) => r.id)

            try {
              const { receiptsApi } = await import('@/api/receipts')
              await receiptsApi.updateReceiptPaymentStatus(receiptIds, 'open')
              completedPayments.add(stepIndex)

              await Swal.fire({
                title: 'Erfolgreich markiert',
                text: 'Quittungen wurden als offen markiert.',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
              })
            } catch (error) {
              console.error('Fehler beim Markieren als offen:', error)
              await Swal.fire({
                title: `${renderToHtml(['fas', 'exclamation-triangle'])} Fehler`,
                text: 'Fehler beim Markieren als offen.',
                icon: 'error',
                confirmButtonText: 'OK',
              })
            }
          }
        },
      })

      return stepResult
    }

    for (let i = 0; i < groupedData.length; i++) {
      currentStep = i
      const stepResult = await showUserStep(groupedData[i], i)

      if (stepResult.isDismissed) {
        break
      }
    }

    if (onComplete) {
      onComplete({
        completedPayments: completedPayments.size,
        totalUsers: groupedData.length,
      })
    }

    return {
      completedPayments: completedPayments.size,
      totalUsers: groupedData.length,
    }
  } catch (error) {
    console.error('Fehler im PayPal Wizard:', error)

    await Swal.fire({
      title: 'Fehler',
      text: error.response?.data?.error || 'Fehler beim Laden der PayPal-Daten',
      icon: 'error',
      confirmButtonText: 'OK',
    })

    if (onCancel) {
      onCancel(error)
    }

    return null
  }
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount)
}

export const showRefundDataModal = async (
  teamName,
  refundSettings,
  currentData = null,
  onComplete,
  onCancel,
) => {
  const colors = getThemeColors()

  const moneyIconHtml = await renderToHtml('font-awesome-icon', {
    icon: 'money-bill-wave',
    class: 'fa-3x text-warning mb-3',
  })
  const paypalIconHtml = await renderToHtml('font-awesome-icon', {
    icon: ['fab', 'paypal'],
    class: 'text-primary me-2',
  })
  const bankIconHtml = await renderToHtml('font-awesome-icon', {
    icon: 'university',
    class: 'text-info me-2',
  })
  const cashIconHtml = await renderToHtml('font-awesome-icon', {
    icon: 'money-bill-wave',
    class: 'text-success me-2',
  })

  const result = await Swal.fire({
    title: 'Erstattungsdaten erforderlich',
    html: `
      <div class="text-center">
        <div class="mb-4">
          ${moneyIconHtml}
          <h5>Erstattungsdaten konfigurieren</h5>
          <p class="text-muted">
            Für den Bereich <strong>${teamName}</strong> musst du deine Erstattungsdaten konfigurieren, 
            bevor du fortfahren kannst.
          </p>
        </div>
        
        <div class="alert alert-info">
          <h6>Verfügbare Erstattungsmethoden:</h6>
          <ul class="list-unstyled mb-0">
            ${refundSettings.paypalEnabled ? `<li>${paypalIconHtml}PayPal.me</li>` : ''}
            ${refundSettings.bankTransferEnabled ? `<li>${bankIconHtml}Banküberweisung</li>` : ''}
            ${refundSettings.cashEnabled ? `<li>${cashIconHtml}Bar</li>` : ''}
          </ul>
        </div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Erstattungsmethode angeben',
    cancelButtonText: 'Später',
    background: colors.background,
    color: colors.text,
    confirmButtonColor: colors.primary,
    cancelButtonColor: colors.secondary,
    width: '500px',
    customClass: {
      popup: 'refund-data-modal-popup',
      title: 'refund-data-modal-title',
      htmlContainer: 'refund-data-modal-content',
    },
  })

  if (result.isConfirmed) {
    router.push(
      `/teams/${currentData?.teamId || ''}/refund?requireSetup=true&redirect=${encodeURIComponent(window.location.pathname)}`,
    )

    if (onComplete) await onComplete()
    return { action: 'redirect' }
  } else {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }
}

function renderToHtml(icon, options = {}) {
  const container = document.createElement('span')
  const app = createApp({
    render() {
      return h(FontAwesomeIcon, { icon, ...options })
    },
  })
  app.component('font-awesome-icon', FontAwesomeIcon)
  app.mount(container)
  const html = container.innerHTML
  app.unmount()
  return html
}

/**
 * Coupon validieren (separate Funktion für SweetAlert-Dialoge)
 */
export const validateCouponInDialog = async (code, purchaseType, originalAmount) => {
  try {
    const { couponsApi } = await import('@/api/coupons')
    const result = await couponsApi.validateCoupon(code, purchaseType, originalAmount)
    return { success: true, data: result }
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Gutschein konnte nicht validiert werden',
    }
  }
}

/**
 * Globale Funktion für Coupon-Validierung in SweetAlert-Dialogen
 */
window.validateCouponInDialog = async function (purchaseType, originalAmount) {
  const couponInput = document.getElementById('coupon-code')
  const validateBtn = document.getElementById('validate-coupon-btn')
  const couponResult = document.getElementById('coupon-result')
  const discountRow = document.getElementById('coupon-discount-row')
  const discountAmount = document.getElementById('discount-amount')
  const finalTotal = document.getElementById('final-total')
  const checkCircleIconHtml = renderToHtml('font-awesome-icon', {
    icon: 'check-circle',
    class: 'me-2',
  })
  const exclamationTriangleIconHtml = renderToHtml('font-awesome-icon', {
    icon: 'exclamation-triangle',
    class: 'me-2',
  })
  const code = couponInput.value.trim().toUpperCase()
  if (!code) return

  validateBtn.disabled = true
  validateBtn.innerHTML = '<div class="spinner-border spinner-border-sm" role="status"></div>'

  try {
    const couponsModule = await import('@/api/coupons')

    const result = await couponsModule.validateCoupon(code, purchaseType, originalAmount)

    const discount = result.discountAmount

    let discountText = ''
    switch (result.coupon.type) {
      case 'free_coins':
        discountText = `${Math.round(result.coupon.value)} kostenlose Quittungen`
        break
      case 'percentage_discount':
        const effectivePercentage = Math.round(result.discountPercentage * 100) / 100
        const percentageText =
          effectivePercentage === result.coupon.value
            ? `${result.coupon.value}%`
            : `${effectivePercentage}%`
        discountText = `${percentageText} Rabatt (${formatCurrency(discount)} gespart)`
        break
      case 'free_team_pro':
        const planText = result.coupon.teamProPlan === 'ultimate' ? 'Ultimate' : 'Pro'
        const durationUnit = result.coupon.teamProDurationUnit || 'months'
        const durationLabels = {
          days: 'Tage',
          months: 'Monate',
          years: 'Jahre',
        }
        const durationLabel = durationLabels[durationUnit] || durationUnit
        const durationText =
          result.coupon.teamProDuration === 1
            ? `1 ${durationLabel.slice(0, -1)}`
            : `${result.coupon.teamProDuration} ${durationLabel}`
        const coinsText =
          result.coupon.teamProCoins > 0
            ? ` + ${result.coupon.teamProCoins} Quittungen (Gesamt)`
            : ''
        discountText = `Kostenloses Bereich ${planText} für ${durationText}${coinsText}`
        break
      default:
        discountText = `Rabatt: ${formatCurrency(discount)}`
    }

    const conditions = []
    if (result.coupon.minPurchaseAmount) {
      conditions.push('Mindestbestellwert: ' + formatCurrency(result.coupon.minPurchaseAmount))
    }
    if (result.coupon.maxDiscountAmount) {
      const effectivePercentage = Math.round(result.discountPercentage * 100) / 100

      if (result.coupon.type === 'percentage_discount') {
        const percentageValue =
          result.coupon.value % 1 === 0 ? Math.floor(result.coupon.value) : result.coupon.value
        conditions.push(
          'Bis zu ' +
            percentageValue +
            '% Rabatt (max. ' +
            formatCurrency(result.coupon.maxDiscountAmount) +
            ')',
        )
      } else {
        conditions.push('Max. Rabatt: ' + formatCurrency(result.coupon.maxDiscountAmount))
      }
    }
    const conditionsText = conditions.length > 0 ? conditions.join(' • ') : ''

    couponResult.innerHTML = `
      <div class="alert alert-success py-2">
        <div class="d-flex align-items-center">
          ${checkCircleIconHtml}
          <div class="text-center w-100">
            <strong>${result.coupon.name}</strong>
            ${result.description ? `<div class="small text-muted mb-1">${result.description}</div>` : ''}
            <div class="small">${discountText}</div>
            ${conditionsText ? `<div class="small text-muted mt-2"><hr class="my-1"><div class="fw-bold mb-1">Bonusbedingungen:</div>${conditionsText}</div>` : ''}
          </div>
        </div>
      </div>
    `

    if (result.coupon.type === 'free_coins') {
      if (discountRow) {
        discountRow.style.display = 'none'
        finalTotal.textContent = formatCurrency(originalAmount)
      }

      const bonusCoins = document.getElementById('bonus-coins')
      const bonusCoinsAmount = document.getElementById('bonus-coins-amount')
      if (bonusCoins && bonusCoinsAmount) {
        bonusCoinsAmount.textContent = Math.round(result.coupon.value)
        bonusCoins.style.display = 'inline'
      }
    } else if (result.coupon.type === 'free_team_pro') {
      if (discountRow) {
        discountRow.style.display = 'flex'
        discountAmount.textContent = formatCurrency(discount)
        finalTotal.textContent = formatCurrency(originalAmount - discount)
      }

      const couponDetails = document.getElementById('coupon-details')
      const couponPlan = document.getElementById('coupon-plan')
      const couponDuration = document.getElementById('coupon-duration')
      const couponCoins = document.getElementById('coupon-coins')

      if (couponDetails && couponPlan && couponDuration && couponCoins) {
        couponPlan.textContent =
          result.coupon.teamProPlan === 'ultimate' ? 'Ultimate Plan' : 'Pro Plan'

        const durationUnit = result.coupon.teamProDurationUnit || 'months'
        const durationLabels = {
          days: 'Tage',
          months: 'Monate',
          years: 'Jahre',
        }
        const durationLabel = durationLabels[durationUnit] || durationUnit
        couponDuration.textContent = `${result.coupon.teamProDuration} ${durationLabel}`

        couponCoins.textContent = `${result.coupon.teamProCoins} Quittungen (Gesamt)`

        couponDetails.style.display = 'block'
      }

      const bonusCoins = document.getElementById('bonus-coins')
      if (bonusCoins) {
        bonusCoins.style.display = 'none'
      }
    } else {
      if (discountRow) {
        discountRow.style.display = 'flex'
        discountAmount.textContent = formatCurrency(discount)
        finalTotal.textContent = formatCurrency(originalAmount - discount)
      }

      const couponDetails = document.getElementById('coupon-details')
      if (couponDetails) {
        couponDetails.style.display = 'none'
      }

      const bonusCoins = document.getElementById('bonus-coins')
      if (bonusCoins) {
        bonusCoins.style.display = 'none'
      }
    }
  } catch (error) {
    console.error('Coupon validation error:', error)
    couponResult.innerHTML = `
      <div class="alert alert-danger py-2">
        <div class="d-flex align-items-center">
          ${exclamationTriangleIconHtml}
          <span class="w-100">${error.response?.data?.message || 'Gutschein konnte nicht validiert werden'}</span>
        </div>
      </div>
    `

    if (discountRow) {
      discountRow.style.display = 'none'
      finalTotal.textContent = formatCurrency(originalAmount)
    }

    const couponDetails = document.getElementById('coupon-details')
    if (couponDetails) {
      couponDetails.style.display = 'none'
    }

    const bonusCoins = document.getElementById('bonus-coins')
    if (bonusCoins) {
      bonusCoins.style.display = 'none'
    }
  } finally {
    validateBtn.disabled = false
    validateBtn.textContent = 'Einlösen'
  }
}

export const showCouponCoinPurchaseModal = async (
  coinPackages,
  lastBillingData = null,
  onComplete,
  onCancel,
  options = {},
) => {
  const colors = getThemeColors()
  const {
    initialCouponCode = '',
    preValidatedCoupon = null,
    validationPurchaseType = 'coin_purchase',
    finalValidationPurchaseType = 'coin_purchase',
  } = options || {}

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  const calculateCustomPrice = async (coins) => {
    try {
      const { calculatePrice } = await import('@/api/billing')
      const data = await calculatePrice(coins)
      return data.totalPrice
    } catch (error) {
      console.error('Fehler bei der Preisberechnung:', error)
      const netPrice = coins * 0.1
      const vatAmount = netPrice * 0.19
      return netPrice + vatAmount
    }
  }

  const Queue = Swal.mixin({
    progressSteps: ['1', '2', '3', '4', '5'],
    confirmButtonText: 'Weiter &rarr;',
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    reverseButtons: true,
    allowOutsideClick: false,
    allowEscapeKey: true,
    background: colors.background,
    color: colors.text,
    customClass: {
      container: 'coin-purchase-modal',
      popup: 'coin-purchase-modal-popup',
      title: 'coin-purchase-modal-title',
      htmlContainer: 'coin-purchase-modal-content',
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary',
    },
  })

  const ticketIconHtml = await renderToHtml('font-awesome-icon', { icon: ['fas', 'ticket'] })
  const checkCircleIconHtml = await renderToHtml('font-awesome-icon', {
    icon: ['fas', 'check-circle'],
  })
  const exclamationTriangleIconHtml = await renderToHtml('font-awesome-icon', {
    icon: ['fas', 'exclamation-triangle'],
  })

  const sanitizedInitialCode =
    initialCouponCode && typeof initialCouponCode === 'string'
      ? initialCouponCode.trim().toUpperCase()
      : ''
  const isPreValidated =
    preValidatedCoupon &&
    preValidatedCoupon.coupon &&
    !preValidatedCoupon.needsRevalidation &&
    preValidatedCoupon.purchaseTypeMismatch !== true

  let couponData = preValidatedCoupon || null
  let validatedCoupon = isPreValidated ? preValidatedCoupon : null
  let codeInput = null
  let resultDiv = null
  let confirmButtonLabel = 'Weiter zur Quittung-Auswahl'
  const initialCouponCodeValue =
    sanitizedInitialCode || (validatedCoupon?.coupon?.code ? validatedCoupon.coupon.code : '')

  const renderCouponSuccess = (result) => {
    if (!result || !result.coupon) {
      return ''
    }

    let discountText = ''
    switch (result.coupon.type) {
      case 'free_coins':
        discountText = `${Math.round(result.coupon.value)} kostenlose Quittungen`
        break
      case 'percentage_discount':
        discountText = `${result.coupon.value}% Rabatt`
        break
      case 'free_team_pro': {
        const durationUnit = result.coupon.teamProDurationUnit || 'months'
        const durationLabels = {
          days: 'Tage',
          months: 'Monate',
          years: 'Jahre',
        }
        const durationLabel = durationLabels[durationUnit] || durationUnit
        const durationText =
          result.coupon.teamProDuration === 1
            ? `1 ${durationLabel.slice(0, -1)}`
            : `${result.coupon.teamProDuration} ${durationLabel}`
        const coinsText =
          result.coupon.teamProCoins > 0
            ? ` + ${result.coupon.teamProCoins} Quittungen (Gesamt)`
            : ''
        discountText = `Kostenloses Bereichs-Upgrade für ${durationText}${coinsText}`
        break
      }
      default:
        discountText = 'Rabatt verfügbar'
    }

    const conditions = []
    if (result.coupon.minPurchaseAmount) {
      conditions.push('Mindestbestellwert: ' + formatCurrency(result.coupon.minPurchaseAmount))
    }
    if (result.coupon.maxDiscountAmount) {
      if (result.coupon.type === 'percentage_discount') {
        const percentageValue =
          result.coupon.value % 1 === 0 ? Math.floor(result.coupon.value) : result.coupon.value
        conditions.push(
          'Bis zu ' +
            percentageValue +
            '% Rabatt (max. ' +
            formatCurrency(result.coupon.maxDiscountAmount) +
            ')',
        )
      } else {
        conditions.push('Max. Rabatt: ' + formatCurrency(result.coupon.maxDiscountAmount))
      }
    }
    const conditionsText = conditions.length > 0 ? conditions.join(' • ') : ''

    return `
      <div class="alert alert-success py-2">
        <div class="d-flex align-items-center w-100">
          ${checkCircleIconHtml}
          <div class="w-100 text-center">
            <strong>${result.coupon.name}</strong><br>
            <small>${discountText}</small>
            ${
              result.description
                ? `<div class="small text-muted">${result.description}</div>`
                : ''
            }
            ${
              conditionsText
                ? `<div class="small text-muted mt-2"><hr class="my-1"><div class="fw-bold mb-1">Bonusbedingungen:</div>${conditionsText}</div>`
                : ''
            }
          </div>
        </div>
      </div>
    `
  }

  const updateConfirmButton = (button, result) => {
    if (!button) return

    if (result?.coupon?.type === 'free_team_pro') {
      button.textContent = 'Weiter zur Bereichsauswahl'
    } else {
      button.textContent = confirmButtonLabel
    }
  }

  const couponResult = await Queue.fire({
    title: `Gutschein eingeben ${ticketIconHtml}`,
    html: `
      <div class="coupon-validation">
        <h6 class="mb-3">Gutscheincode eingeben</h6>
        <div class="mb-3">
          <label class="form-label">Gutscheincode</label>
          <div class="input-group w-100">
            <input
              type="text"
              class="form-control"
              id="coupon-code-input"
              placeholder="Gutscheincode eingeben"
            />
            <button
              class="btn btn-outline-secondary"
              type="button"
              id="validate-coupon-button"
            >
              Einlösen
            </button>
          </div>
          <div id="coupon-validation-result" class="mt-2 text-center w-100"></div>
        </div>
      </div>
    `,
    currentProgressStep: 0,
    confirmButtonText: confirmButtonLabel,
    preConfirm: () => {
       if(!validatedCoupon && resultDiv && resultDiv.innerHTML !== '') {
        Swal.showValidationMessage('Gutscheincode ist nicht gültig!')
        return false
      }else if (!validatedCoupon) {
        Swal.showValidationMessage('Bitte validiere zuerst einen Gutscheincode')
        return false
      }
      return { couponCode: validatedCoupon.coupon.code, couponData: validatedCoupon }
    },
    didOpen: (popup) => {
      const confirmBtn = Swal.getConfirmButton()
      updateConfirmButton(confirmBtn, validatedCoupon)
      setTimeout(() => {
        const validateBtn = popup.querySelector('#validate-coupon-button')
        codeInput = popup.querySelector('#coupon-code-input')
        resultDiv = popup.querySelector('#coupon-validation-result')

        if (codeInput && initialCouponCodeValue) {
          codeInput.value = initialCouponCodeValue
        }

        if (validatedCoupon && resultDiv) {
          resultDiv.innerHTML = renderCouponSuccess(validatedCoupon)
        }

        validateBtn.onclick = async () => {
          Swal.resetValidationMessage()

          const code = codeInput.value.trim().toUpperCase()
          if (!code) {
            if (resultDiv) {
              resultDiv.innerHTML = `
                <div class="alert alert-gradient-warning py-2">
                  Bitte gib einen Gutscheincode ein
                </div>
              `
            }
            return
          }

          validateBtn.disabled = true
          validateBtn.innerHTML =
            '<div class="spinner-border spinner-border-sm" role="status"></div>'

          try {
            const { couponsApi } = await import('@/api/coupons')

            const result = await couponsApi.validateCoupon(
              code,
              validationPurchaseType,
              0,
            )

            validatedCoupon = result
            couponData = result
            updateConfirmButton(confirmBtn, result)

            if (resultDiv) {
              resultDiv.innerHTML = renderCouponSuccess(result)
            }
          } catch (error) {
            const errorMessage =
              error.response?.data?.message || 'Gutschein konnte nicht validiert werden'

            couponData = null

            if (
              errorMessage.includes('nicht gefunden') ||
              errorMessage.includes('not found') ||
              errorMessage.includes('abgelaufen') ||
              errorMessage.includes('expired') ||
              errorMessage.includes('maximal oft verwendet')
            ) {
              validatedCoupon = null
              if (resultDiv) {
                resultDiv.innerHTML = `
                  <div class="alert alert-danger py-2">
                    <div class="d-flex align-items-center">
                      ${exclamationTriangleIconHtml}
                      <span class="ms-2">${errorMessage}</span>
                    </div>
                  </div>
                `
              }
              updateConfirmButton(confirmBtn, null)
            } else {
              validatedCoupon = {
                coupon: { code: code, name: code, type: 'unknown' },
                needsRevalidation: true,
                preliminaryError: errorMessage,
              }
              couponData = validatedCoupon

              if (resultDiv) {
                resultDiv.innerHTML = `
                  <div class="alert alert-danger py-2">
                    <div class="d-flex align-items-center">
                      ${exclamationTriangleIconHtml}
                      <div>
                        <strong>Gutschein fehlgeschlagen</strong><br>
                        <div class="small text-muted">Hinweis: ${errorMessage}</div>
                      </div>
                    </div>
                  </div>
                `
              }
              updateConfirmButton(confirmBtn, null)
            }
          } finally {
            validateBtn.disabled = false
            validateBtn.textContent = 'Einlösen'
          }
        }
      }, 200)
    },
  })

  if (couponResult.isDismissed) {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }

  const couponCode = couponResult.value.couponCode
  couponData = couponResult.value.couponData

  
  if (couponData?.coupon?.type === 'free_coins') {
    const confirmResult = await Queue.fire({
      title: `Kostenlose Quittungen - Bestätigung ${checkCircleIconHtml}`,
      html: `
        <div class="purchase-summary">
          <h6 class="mb-3">Gutschein-Einlösung</h6>
          
          <div class="alert alert-success mb-3">
            <strong>Gutschein aktiviert:</strong> ${couponData.coupon.name}
            ${couponData.description ? `<br><small>${couponData.description}</small>` : ''}
          </div>
          
          <div class="alert alert-info">
            <div class="row">
              <div class="col-6"><strong>Kostenlose Quittungen:</strong></div>
              <div class="col-6 text-end">
                <span class="text-success">${Math.round(couponData.coupon.value)}</span>
              </div>
            </div>
            <hr>
            <div class="row">
              <div class="col-6"><strong>Gesamtbetrag:</strong></div>
              <div class="col-6 text-end"><strong class="text-success">Kostenlos</strong></div>
            </div>
          </div>
          
          <div class="mt-3">
            <p class="text-muted">
              <font-awesome-icon icon="info-circle" class="me-1" />
              Die kostenlosen Quittungen werden sofort Ihrem Konto gutgeschrieben.
            </p>
          </div>
        </div>
      `,
      currentProgressStep: 4,
      confirmButtonText: 'Kostenlose Quittungen einlösen',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const { couponsApi } = await import('@/api/coupons')
          const finalCouponValidation = await couponsApi.validateCoupon(
            couponCode,
            finalValidationPurchaseType,
            0, 
          )

          return {
            couponCode,
            couponData: finalCouponValidation,
            finalPrice: 0,
          }
        } catch (error) {
          Swal.showValidationMessage(
            `Gutschein-Fehler: ${error.response?.data?.message || 'Gutschein konnte nicht angewendet werden'}`,
          )
          return false
        }
      },
    })

    if (confirmResult.isConfirmed) {
      if (onComplete) {
        await onComplete({
          coins: 0, 
          billingData: null, 
          priceData: {
            netPrice: 0,
            vatAmount: 0,
            totalPrice: 0,
            pricePerCoin: 0,
          },
          couponCode: couponCode,
          paymentMethod: 'free', 
        })
      }

      return {
        action: 'complete',
        coins: 0,
        billingData: null,
        priceData: {
          netPrice: 0,
          vatAmount: 0,
          totalPrice: 0,
          pricePerCoin: 0,
        },
        couponCode: couponCode,
        paymentMethod: 'free',
      }
    } else {
      if (onCancel) await onCancel()
      return { action: 'cancel' }
    }
  }

  if (couponData?.coupon?.type === 'free_team_pro') {
    const durationUnitLabels = {
      days: 'Tage',
      months: 'Monate',
      years: 'Jahre',
    }

    const planLabel =
      couponData.coupon.teamProPlan === 'ultimate' ? 'Ultimate Plan' : 'Pro Plan'

    const durationValue = couponData.coupon.teamProDuration || 1
    const durationUnit = durationUnitLabels[couponData.coupon.teamProDurationUnit] || 'Monate'
    const coinsIncluded = couponData.coupon.teamProCoins || 0

    let availableTeams = Array.isArray(couponData.eligibleTeams) ? couponData.eligibleTeams : null

    if (!availableTeams) {
      try {
        const { teamService } = await import('@/api/teams')
        const response = await teamService.getEligibleTeamsForUpgrade()
        availableTeams = response?.eligibleTeams || response || []
      } catch (error) {
        console.error('Fehler beim Laden der verfügbaren Bereiche:', error)
        availableTeams = []
      }
    }

    if (!availableTeams || availableTeams.length === 0) {
      const teamRedirect = await Swal.fire({
        icon: 'info',
        title: 'Kein Bereich verfügbar',
        html: `
          <p class="mb-0">Es steht aktuell kein Bereich zur Verfügung, der mit diesem Gutschein upgegradet werden kann.</p>
          <p class="mb-0">Bitte erstelle zuerst einen Bereich unter <strong>Bereiche</strong>.</p>
        `,
        confirmButtonText: 'Zu Bereiche',
        cancelButtonText: 'Abbrechen',
        showCancelButton: true,
        allowOutsideClick: false,
      })

      if (teamRedirect.isConfirmed) {
        router.push('/teams')
      }

      if (onCancel) await onCancel()
      return { action: 'cancel' }
    }

    const selectionResult = await Queue.fire({
      title: 'Bereich für Gutschein auswählen',
      html: `
        <div class="team-selection">
          <div class="alert alert-success mb-3">
            <strong>Gutschein aktiviert:</strong> ${couponData.coupon.name}
            ${
              couponData.description
                ? `<div class="small text-muted mt-1">${couponData.description}</div>`
                : ''
            }
          </div>
          <div class="alert alert-info">
            <div><strong>Plan:</strong> ${planLabel}</div>
            <div><strong>Laufzeit:</strong> ${durationValue} ${durationUnit}</div>
            ${
              coinsIncluded > 0
                ? `<div><strong>Quittungen pro Monat:</strong> ${coinsIncluded}</div>`
                : ''
            }
          </div>
          <div class="mt-3">
            <h6 class="mb-2">Bereich auswählen:</h6>
            <div class="list-group" role="listbox">
              ${availableTeams
                .map(
                  (team, index) => `
                <label class="list-group-item list-group-item-action d-flex justify-content-between align-items-center team-selection-item" data-team-id="${team.id}">
                  <div>
                    <div class="fw-semibold">${team.name}</div>
                    <div class="small text-muted">Status: ${
                      team.status === 'free' ? 'Kostenlos' : team.status
                    }</div>
                  </div>
                  <div class="form-check mb-0">
                    <input class="form-check-input" type="radio" name="team-selection" value="${
                      team.id
                    }" ${index === 0 ? 'checked' : ''} />
                  </div>
                </label>
              `,
                )
                .join('')}
            </div>
          </div>
        </div>
      `,
      currentProgressStep: 1,
      confirmButtonText: 'Gutschein anwenden',
      preConfirm: async () => {
        const selectedRadio = document.querySelector('input[name="team-selection"]:checked')

        if (!selectedRadio) {
          Swal.showValidationMessage('Bitte wähle einen Bereich aus')
          return false
        }

        const selectedTeamId = selectedRadio.value
        const selectedTeam = availableTeams.find((team) => team.id === selectedTeamId)

        try {
          const { couponsApi } = await import('@/api/coupons')
          const finalValidation = await couponsApi.validateCoupon(
            couponCode,
            'team_upgrade',
            0,
            selectedTeamId,
          )

          return {
            action: 'teamUpgrade',
            teamId: selectedTeamId,
            teamName: selectedTeam?.name || '',
            couponCode,
            couponData: finalValidation,
            plan: couponData.coupon.teamProPlan === 'ultimate' ? 'team_ultimate' : 'team_pro',
            duration: durationValue,
            durationUnit: couponData.coupon.teamProDurationUnit,
            coins: coinsIncluded,
          }
        } catch (error) {
          const message =
            error.response?.data?.message || 'Gutschein konnte nicht für den Bereich angewendet werden'
          Swal.showValidationMessage(message)
          return false
        }
      },
      didOpen: (popup) => {
        const items = popup.querySelectorAll('.team-selection-item')
        items.forEach((item) => {
          item.addEventListener('click', (event) => {
            const radio = item.querySelector('input[type="radio"]')
            if (radio) {
              radio.checked = true
            }
            items.forEach((other) => other.classList.remove('active'))
            item.classList.add('active')
          })
        })

        const initiallyChecked = popup.querySelector('input[name="team-selection"]:checked')
        if (initiallyChecked) {
          const parentItem = initiallyChecked.closest('.team-selection-item')
          parentItem && parentItem.classList.add('active')
        }
      },
    })

    if (selectionResult.isConfirmed) {
      if (onComplete) {
        await onComplete(selectionResult.value)
      }

      return {
        ...selectionResult.value,
      }
    }

    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }

  const coinIconHtml = await renderToHtml('font-awesome-icon', { icon: ['fas', 'coins'] })
  const calculatorIconHtml = await renderToHtml('font-awesome-icon', {
    icon: ['fas', 'calculator'],
  })
  const euroIconHtml = await renderToHtml('font-awesome-icon', { icon: ['fas', 'euro-sign'] })

  const coinSelectionResult = await Queue.fire({
    title: `Quittungen kaufen - Auswahl ${coinIconHtml}`,
    html: `
      <div class="coin-selection">
        <div class="alert alert-success mb-3">
          <strong>Gutschein aktiviert:</strong> ${couponData.coupon.name}
        </div>
        <h6 class="mb-3">Anzahl Quittungen auswählen</h6>
        <!-- Quittung Packages -->
        <div class="row g-3 mb-4">
          ${coinPackages
            .map(
              (pkg) => `
            <div class="col-6">
              <div class="card coin-package" data-coins="${pkg.coins}" style="cursor: pointer; border: 2px solid var(--bs-border-color, #dee2e6); transition: border-color 0.15s ease-in-out;">
                <div class="card-body text-center">
                  ${coinIconHtml}
                  <h5 class="card-title text-primary">${pkg.coins} Quittungen</h5>
                  <p class="card-text text-muted">${formatCurrency(pkg.price)}</p>
                  <small class="text-muted">${euroIconHtml} ${formatCurrency(pkg.pricePerCoin)} pro Quittung</small>
                </div>
              </div>
            </div>
          `,
            )
            .join('')}
        </div>
        <!-- Custom Amount (jetzt mit min="0") -->
        <div class="mt-3">
          <label class="form-label">Oder individuelle Anzahl: ${calculatorIconHtml}</label>
          <div class="input-group">
            <input 
              type="number" 
              class="form-control" 
              id="custom-coins"
              min="0"
              placeholder="Anzahl Quittungen"
            >
            <span class="input-group-text">Quittungen</span>
          </div>
          <div id="custom-price" class="text-muted mt-1" style="display: none;"></div>
        </div>
      </div>
    `,
    currentProgressStep: 1,
    preConfirm: () => {
      const selectedPackage = document.querySelector('.coin-package.selected')
      const customCoins = document.getElementById('custom-coins').value
      if (couponData?.coupon?.type === 'free_coins') {
        return { type: 'custom', coins: 0, pricePerCoin: 0.1 }
      } else if (selectedPackage) {
        const coins = parseInt(selectedPackage.dataset.coins)
        const pkg = coinPackages.find((p) => p.coins === coins)
        return { type: 'package', coins, pricePerCoin: pkg.pricePerCoin }
      } else if (customCoins !== '' && parseInt(customCoins) >= 0) {
        return { type: 'custom', coins: parseInt(customCoins), pricePerCoin: 0.1 }
      }

      if (couponData?.coupon?.type === 'free_coins') {
        Swal.showValidationMessage(
          'Bitte wähle eine Quittung-Anzahl aus (auch 0 oder leer ist möglich)',
        )
      } else {
        Swal.showValidationMessage('Bitte wähle eine Quittung-Anzahl aus (0 oder mehr)')
      }
      return false
    },
    didOpen: (popup) => {
      setTimeout(() => {
        const packages = popup.querySelectorAll('.coin-package')
        packages.forEach((pkg) => {
          pkg.addEventListener('click', () => {
            packages.forEach((p) => {
              p.style.borderColor = 'var(--bs-border-color, #dee2e6)'
              p.classList.remove('selected')
            })

            pkg.style.borderColor = 'var(--color-primary)'
            pkg.classList.add('selected')

            document.getElementById('custom-coins').value = ''
            document.getElementById('custom-price').style.display = 'none'

            document.getElementById('custom-coins').value = pkg.dataset.coins
          })
        })

        const customInput = popup.querySelector('#custom-coins')
        let priceCalculationTimeout
        customInput.addEventListener('input', (e) => {
          const coins = parseInt(e.target.value)
          if (coins >= 0 || e.target.value === '') {
            packages.forEach((p) => {
              p.style.borderColor = 'var(--bs-border-color, #dee2e6)'
              p.classList.remove('selected')
            })

            clearTimeout(priceCalculationTimeout)
            priceCalculationTimeout = setTimeout(async () => {
              if (coins === 0 || e.target.value === '') {
                document.getElementById('custom-price').textContent =
                  'Gesamtpreis: ' + formatCurrency(0)
              } else if (coins > 0) {
                const price = await calculateCustomPrice(coins)
                document.getElementById('custom-price').textContent =
                  'Gesamtpreis: ' + formatCurrency(price)
              }
              document.getElementById('custom-price').style.display = 'block'
            }, 300)
          } else {
            document.getElementById('custom-price').style.display = 'none'
          }
        })
      }, 200)
    },
  })

  if (coinSelectionResult.isDismissed) {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }

  const selectedCoins = coinSelectionResult.value

  const billingResult = await Queue.fire({
    title: 'Quittungen kaufen - Rechnungsdaten',
    html: `
      <div class="billing-data">
        <h6 class="mb-3">Rechnungsdaten</h6>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Vorname *</label>
            <input type="text" class="form-control" id="firstName" value="${lastBillingData?.firstName || ''}" required>
          </div>
          <div class="col-md-6">
            <label class="form-label">Nachname *</label>
            <input type="text" class="form-control" id="lastName" value="${lastBillingData?.lastName || ''}" required>
          </div>
          <div class="col-12">
            <label class="form-label">Unternehmen (optional)</label>
            <input type="text" class="form-control" id="company" value="${lastBillingData?.company || ''}">
          </div>
          <div class="col-md-8">
            <label class="form-label">Straße *</label>
            <input type="text" class="form-control" id="street" value="${lastBillingData?.street || ''}" required>
          </div>
          <div class="col-md-4">
            <label class="form-label">Hausnummer *</label>
            <input type="text" class="form-control" id="number" value="${lastBillingData?.number || ''}" required>
          </div>
          <div class="col-md-4">
            <label class="form-label">PLZ *</label>
            <input type="text" class="form-control" id="zip" value="${lastBillingData?.zip || ''}" required>
          </div>
          <div class="col-md-8">
            <label class="form-label">Stadt *</label>
            <input type="text" class="form-control" id="city" value="${lastBillingData?.city || ''}" required>
          </div>
          <div class="col-12">
            <label class="form-label">Land *</label>
            <select class="form-select" id="country" required>
              <option value="">Land auswählen</option>
              <option value="Deutschland" ${lastBillingData?.country === 'Deutschland' ? 'selected' : ''}>Deutschland</option>
            </select>
          </div>
          <div class="col-12">
            <label class="form-label">E-Mail-Adresse *</label>
            <input type="email" class="form-control" id="email" value="${lastBillingData?.email || ''}" required>
            <div class="form-text">Die Zahlungsaufforderung bzw. Rechnung wird an diese E-Mail-Adresse gesendet.</div>
          </div>
          <div class="col-12">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="saveForFuture" checked>
              <label class="form-check-label" for="saveForFuture">
                Speichere Rechnungsdaten für zukünftige Einkäufe
              </label>
            </div>
          </div>
        </div>
      </div>
    `,
    currentProgressStep: 2,
    preConfirm: () => {
      const billingData = {
        firstName: document.getElementById('firstName').value.trim(),
        lastName: document.getElementById('lastName').value.trim(),
        company: document.getElementById('company').value.trim(),
        street: document.getElementById('street').value.trim(),
        number: document.getElementById('number').value.trim(),
        zip: document.getElementById('zip').value.trim(),
        city: document.getElementById('city').value.trim(),
        country: document.getElementById('country').value,
        email: document.getElementById('email').value.trim(),
      }

      if (
        !billingData.firstName ||
        !billingData.lastName ||
        !billingData.street ||
        !billingData.number ||
        !billingData.zip ||
        !billingData.city ||
        !billingData.country ||
        !billingData.email
      ) {
        Swal.showValidationMessage('Bitte fülle alle Pflichtfelder aus')
        return false
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(billingData.email)) {
        Swal.showValidationMessage('Bitte gib eine gültige E-Mail-Adresse ein')
        return false
      }

      
      const saveForFuture = document.getElementById('saveForFuture').checked
      if (saveForFuture) {
        
        const invoiceDataToSave = {
          firstName: billingData.firstName,
          lastName: billingData.lastName,
          company: billingData.company,
          street: billingData.street,
          number: billingData.number,
          zip: billingData.zip,
          city: billingData.city,
          country: billingData.country,
          email: billingData.email
        }
        
        
        import('@/api/invoiceData').then(({ saveInvoiceData }) => {
          saveInvoiceData(invoiceDataToSave).catch(error => {
            console.warn('Fehler beim Speichern der Rechnungsdaten:', error)
          })
        })
      }

      return billingData
    },
  })

  if (billingResult.isDismissed) {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }

  const billingData = billingResult.value

  let netPrice = 0,
    vatAmount = 0,
    totalPrice = 0,
    pricePerCoin = 0

  if (selectedCoins.coins === 0) {
    netPrice = 0
    vatAmount = 0
    totalPrice = 0
    pricePerCoin = 0
  } else if (selectedCoins.type === 'package') {
    try {
      const { calculatePrice } = await import('@/api/billing')
      const priceData = await calculatePrice(selectedCoins.coins)
      netPrice = parseFloat(priceData.netPrice) || 0
      vatAmount = parseFloat(priceData.vatAmount) || 0
      totalPrice = parseFloat(priceData.totalPrice) || 0
      pricePerCoin = parseFloat(priceData.pricePerCoin) || 0
    } catch (error) {
      console.error('Fehler bei der Preisberechnung für Package:', error)
      pricePerCoin = parseFloat(selectedCoins.pricePerCoin) || 0.1
      netPrice = selectedCoins.coins * pricePerCoin
      vatAmount = netPrice * 0.19
      totalPrice = netPrice + vatAmount
    }
  } else {
    try {
      const { calculatePrice } = await import('@/api/billing')
      const priceData = await calculatePrice(selectedCoins.coins)
      netPrice = parseFloat(priceData.netPrice) || 0
      vatAmount = parseFloat(priceData.vatAmount) || 0
      totalPrice = parseFloat(priceData.totalPrice) || 0
      pricePerCoin = parseFloat(priceData.pricePerCoin) || 0
    } catch (error) {
      console.error('Fehler bei der Preisberechnung:', error)
      pricePerCoin = 0.1
      netPrice = selectedCoins.coins * pricePerCoin
      vatAmount = netPrice * 0.19
      totalPrice = netPrice + vatAmount
    }
  }

  let finalCouponValidation = null

  const confirmResult = await Queue.fire({
    title: `Quittungen kaufen - Bestätigung ${checkCircleIconHtml}`,
    html: `
      <div class="purchase-summary">
        <h6 class="mb-3">Bestellzusammenfassung</h6>
        
        <!-- Gutschein-Info -->
        <div class="alert alert-info mb-3" id="coupon-status">
          <strong>Gutschein wird validiert...</strong>
        </div>
        
        <div class="alert alert-info" id="price-summary">
          <!-- Wird dynamisch gefüllt -->
        </div>
        
        <div class="mt-3">
          <h6>Rechnungsadresse:</h6>
          <div class="text-start">
            ${billingData.company ? `<div><strong>${billingData.company}</strong></div>` : ''}
            <div>${billingData.firstName} ${billingData.lastName}</div>
            <div>${billingData.street} ${billingData.number}</div>
            <div>${billingData.zip} ${billingData.city}</div>
            <div>${billingData.country}</div>
            <div class="mt-2"><strong>E-Mail:</strong> ${billingData.email}</div>
          </div>
        </div>
        
        <!-- Widerrufsbelehrung -->
        <div class="mt-4">
          <div class="form-check mb-3">
            <input class="form-check-input" type="checkbox" id="revocation-read" required>
            <label class="form-check-label" for="revocation-read">
              Ich habe die <a href="https://quitly.de/datenschutz/widerrufsbelerhrung" target="_blank" class="text-decoration-underline">Widerrufsbelehrung</a> gelesen.
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="revocation-consent" required>
            <label class="form-check-label" for="revocation-consent">
              Ich stimme ausdrücklich zu, dass mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist begonnen wird. Mir ist bekannt, dass ich mit Beginn der Ausführung mein Widerrufsrecht verliere.
            </label>
          </div>
        </div>
      </div>
    `,
    currentProgressStep: 3,
    confirmButtonText: 'Jetzt kostenpflichtig kaufen',
    showLoaderOnConfirm: true,
    preConfirm: async () => {
      const revocationRead = document.querySelector('#revocation-read')
      const revocationConsent = document.querySelector('#revocation-consent')
      
      if (!revocationRead.checked) {
        Swal.showValidationMessage('Bitte bestätige, dass du die Widerrufsbelehrung gelesen hast')
        return false
      }
      
      if (!revocationConsent.checked) {
        Swal.showValidationMessage('Bitte bestätige deine Zustimmung zur sofortigen Vertragsausführung')
        return false
      }
      
      try {
        const { couponsApi } = await import('@/api/coupons')
        finalCouponValidation = await couponsApi.validateCoupon(
          couponCode,
          finalValidationPurchaseType,
          totalPrice,
        )

        return {
          couponCode,
          couponData: finalCouponValidation,
          finalPrice: Math.max(0, totalPrice - (finalCouponValidation.discountAmount || 0)),
        }
      } catch (error) {
        Swal.showValidationMessage(
          `Gutschein-Fehler: ${error.response?.data?.message || 'Gutschein konnte nicht angewendet werden'}`,
        )
        return false
      }
    },
    didOpen: async (popup) => {
      const couponStatus = popup.querySelector('#coupon-status')
      const priceSummary = popup.querySelector('#price-summary')

      try {
        const { couponsApi } = await import('@/api/coupons')
        finalCouponValidation = await couponsApi.validateCoupon(
          couponCode,
          finalValidationPurchaseType,
          totalPrice,
        )

        const finalPrice = Math.max(0, totalPrice - (finalCouponValidation.discountAmount || 0))

        couponStatus.innerHTML = `
          <div class="alert alert-success mb-0">
            <strong>Gutschein aktiviert:</strong> ${finalCouponValidation.coupon.name}
            ${finalCouponValidation.description ? `<br><small>${finalCouponValidation.description}</small>` : ''}
          </div>
        `

        priceSummary.innerHTML = `
          <div class="row">
            <div class="col-6"><strong>Quittungen:</strong></div>
            <div class="col-6 text-end">
              ${selectedCoins.coins > 0 ? `<span>${selectedCoins.coins}</span>` : ''}
              ${finalCouponValidation.coupon.type === 'free_coins' ? ` <span class="text-success">${selectedCoins.coins > 0 ? '+ ' : ''}${Math.round(finalCouponValidation.coupon.value)} Bonus</span>` : ''}
            </div>
          </div>
          ${
            selectedCoins.coins > 0
              ? `
          <div class="row">
            <div class="col-6"><strong>Preis pro Quittung:</strong></div>
            <div class="col-6 text-end">${formatCurrency(pricePerCoin || 0)}</div>
          </div>
          <div class="row">
            <div class="col-6"><strong>Netto:</strong></div>
            <div class="col-6 text-end">${formatCurrency(netPrice || 0)}</div>
          </div>
          <div class="row">
            <div class="col-6"><strong>MwSt. (19%):</strong></div>
            <div class="col-6 text-end">${formatCurrency(vatAmount || 0)}</div>
          </div>
          `
              : ''
          }
          ${
            finalCouponValidation.discountAmount && finalCouponValidation.discountAmount > 0
              ? `
          <div class="row">
            <div class="col-6"><strong>Rabatt:</strong></div>
            <div class="col-6 text-end text-success">-${formatCurrency(finalCouponValidation.discountAmount)}</div>
          </div>
          `
              : ''
          }
          <hr>
          <div class="row">
            <div class="col-6"><strong>Gesamtbetrag:</strong></div>
            <div class="col-6 text-end"><strong>${formatCurrency(finalPrice)}</strong></div>
          </div>
        `
      } catch (error) {
        couponStatus.innerHTML =
          '<div class="alert alert-gradient-warning mb-3">' +
          '<div class="d-flex align-items-center">' +
          exclamationTriangleIconHtml +
          '<div>' +
          '<strong>Gutschein-Problem:</strong> ' +
          (error.response?.data?.message || 'Gutschein konnte nicht angewendet werden') +
          '</div>' +
          '</div>' +
          '</div>'

        priceSummary.innerHTML =
          '<div class="row">' +
          '<div class="col-6"><strong>Netto:</strong></div>' +
          '<div class="col-6 text-end">' +
          formatCurrency(netPrice || 0) +
          '</div>' +
          '</div>' +
          '<div class="row">' +
          '<div class="col-6"><strong>MwSt. (19%):</strong></div>' +
          '<div class="col-6 text-end">' +
          formatCurrency(vatAmount || 0) +
          '</div>' +
          '</div>' +
          '<hr>' +
          '<div class="row">' +
          '<div class="col-6"><strong>Gesamtbetrag:</strong></div>' +
          '<div class="col-6 text-end"><strong>' +
          formatCurrency(totalPrice) +
          '</strong></div>' +
          '</div>' +
          '<p class="text-warning small mt-2">Gutschein konnte nicht angewendet werden. Preis ohne Rabatt.</p>'
      }
    },
  })

  if (confirmResult.isConfirmed) {
    
    const paymentMethodIconHtml = await renderToHtml('font-awesome-icon', {
      icon: ['fas', 'credit-card'],
    })
    const paypalIconHtml = await renderToHtml('font-awesome-icon', { icon: ['fab', 'paypal'] })
    const bankIconHtml = await renderToHtml('font-awesome-icon', { icon: ['fas', 'university'] })

    const paymentResult = await Queue.fire({
      title: `Quittungen kaufen - Zahlungsmethode ${paymentMethodIconHtml}`,
      html: `
        <div class="payment-method-selection">
          <h6 class="mb-3">Zahlungsmethode auswählen</h6>
          
          <div class="payment-info-card">
            <div class="payment-info-header">
              ${paymentMethodIconHtml}
              <strong>Zahlungsinformationen</strong>
            </div>
            <div class="payment-info-content">
              <div class="payment-method-item payment-method-card" data-method="paypal">
                <div class="payment-method-icon">
                  ${paypalIconHtml}
                </div>
                <div class="payment-method-details w-100 text-center">
                  <strong>PayPal</strong>
                  <span class="payment-description">Sofortige Zahlung über PayPal</span>
                  <small class="text-muted d-block mt-1">Sicher und schnell</small>
                </div>
              </div>
              <div class="payment-method-item payment-method-card" data-method="bank_transfer">
                <div class="payment-method-icon">
                  ${bankIconHtml}
                </div>
                <div class="payment-method-details w-100 text-center">
                  <strong>Überweisung</strong>
                  <span class="payment-description">Klassische Banküberweisung</span>
                  <small class="text-muted d-block mt-1">Zahlungsaufforderung wird per E-Mail gesendet</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Widerrufsbelehrung -->
        <div class="mt-4">
          <div class="form-check mb-3">
            <input class="form-check-input" type="checkbox" id="revocation-read" required>
            <label class="form-check-label" for="revocation-read">
              Ich habe die <a href="https://quitly.de/datenschutz/widerrufsbelerhrung" target="_blank" class="text-decoration-underline">Widerrufsbelehrung</a> gelesen.
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="revocation-consent" required>
            <label class="form-check-label" for="revocation-consent">
              Ich stimme ausdrücklich zu, dass mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist begonnen wird. Mir ist bekannt, dass ich mit Beginn der Ausführung mein Widerrufsrecht verliere.
            </label>
          </div>
        </div>
        </div>
      `,
      currentProgressStep: 4,
      confirmButtonText: 'Jetzt kostenpflichtig kaufen',
      preConfirm: () => {
        const selectedMethod = document.querySelector('.payment-method-card.selected')
        if (!selectedMethod) {
          Swal.showValidationMessage('Bitte wähle eine Zahlungsmethode aus')
          return false
        }
        
        const revocationRead = document.querySelector('#revocation-read')
        const revocationConsent = document.querySelector('#revocation-consent')
        
        if (!revocationRead.checked) {
          Swal.showValidationMessage('Bitte bestätige, dass du die Widerrufsbelehrung gelesen hast')
          return false
        }
        
        if (!revocationConsent.checked) {
          Swal.showValidationMessage('Bitte bestätige deine Zustimmung zur sofortigen Vertragsausführung')
          return false
        }
        
        return { paymentMethod: selectedMethod.dataset.method }
      },
      didOpen: (popup) => {
        const methodCards = popup.querySelectorAll('.payment-method-card')
        methodCards.forEach((card) => {
          card.addEventListener('click', () => {
            methodCards.forEach((c) => {
              c.style.borderColor = 'var(--bs-border-color, #dee2e6)'
              c.classList.remove('selected')
            })

            card.style.borderColor = 'var(--color-primary)'
            card.classList.add('selected')
            
            
            Swal.resetValidationMessage()
          })
        })

        
        const revocationCheckboxes = popup.querySelectorAll('#revocation-read, #revocation-consent')
        revocationCheckboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', () => {
            Swal.resetValidationMessage()
          })
        })
      },
    })

    if (paymentResult.isDismissed) {
      if (onCancel) await onCancel()
      return { action: 'cancel' }
    }

    if (onComplete) {
      await onComplete({
        coins: selectedCoins.coins,
        billingData: billingData,
        priceData: {
          netPrice,
          vatAmount,
          totalPrice: Math.max(0, totalPrice - (finalCouponValidation.discountAmount || 0)),
          pricePerCoin,
        },
        couponCode: couponCode,
        paymentMethod: paymentResult.value?.paymentMethod,
      })
    }

    return {
      action: 'complete',
      coins: selectedCoins.coins,
      billingData: billingData,
      priceData: {
        netPrice,
        vatAmount,
        totalPrice: Math.max(0, totalPrice - (finalCouponValidation.discountAmount || 0)),
        pricePerCoin,
      },
      couponCode: couponCode,
      paymentMethod: paymentResult.value?.paymentMethod,
    }
  } else {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }
}

export const showSellModal = async (onComplete, onCancel) => {
  const colors = getThemeColors()

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  const euroIconHtml = await renderToHtml('font-awesome-icon', { icon: ['fas', 'euro-sign'] })
  const creditCardIconHtml = await renderToHtml('font-awesome-icon', {
    icon: ['fas', 'credit-card'],
  })
  const checkCircleIconHtml = await renderToHtml('font-awesome-icon', {
    icon: ['fas', 'check-circle'],
  })

  const Queue = Swal.mixin({
    progressSteps: ['1', '2', '3'],
    confirmButtonText: 'Weiter &rarr;',
    showCancelButton: true,
    cancelButtonText: 'Abbrechen',
    reverseButtons: true,
    allowOutsideClick: false,
    allowEscapeKey: true,
    background: colors.background,
    color: colors.text,
    customClass: {
      container: 'sell-modal',
      popup: 'sell-modal-popup',
      title: 'sell-modal-title',
      htmlContainer: 'sell-modal-content',
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary',
    },
  })

  const productResult = await Queue.fire({
    title: `Verkaufen - Produkt ${euroIconHtml}`,
    html: `
      <div class="product-details">
        <h6 class="mb-3">Produktdetails</h6>
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Produktbeschreibung *</label>
            <input type="text" class="form-control" id="product-description" placeholder="z.B. Beratungsleistung, Produkt, Dienstleistung" required>
          </div>
          <div class="col-md-6">
            <label class="form-label">Betrag (€) *</label>
            <div class="input-group">
              <input type="number" class="form-control" id="product-amount" min="0.01" step="0.01" placeholder="0.00" required>
              <span class="input-group-text">€</span>
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label">Kunden-E-Mail (optional)</label>
            <input type="email" class="form-control" id="customer-email" placeholder="kunde@example.com">
          </div>
          <div class="col-md-6">
            <label class="form-label">Kunden-Vorname (optional)</label>
            <input type="text" class="form-control" id="customer-firstname" placeholder="Vorname">
          </div>
          <div class="col-md-6">
            <label class="form-label">Kunden-Nachname (optional)</label>
            <input type="text" class="form-control" id="customer-lastname" placeholder="Nachname">
          </div>
        </div>
        
        <!-- Kundenadresse (optional) -->
        <div class="mt-3">
          <h6>Kundenadresse (optional)</h6>
          <div class="row g-3">
            <div class="col-md-8">
              <label class="form-label">Straße</label>
              <input type="text" class="form-control" id="customer-street" placeholder="Straße">
            </div>
            <div class="col-md-4">
              <label class="form-label">Hausnummer</label>
              <input type="text" class="form-control" id="customer-number" placeholder="Nr.">
            </div>
            <div class="col-md-4">
              <label class="form-label">PLZ</label>
              <input type="text" class="form-control" id="customer-zip" placeholder="PLZ">
            </div>
            <div class="col-md-8">
              <label class="form-label">Stadt</label>
              <input type="text" class="form-control" id="customer-city" placeholder="Stadt">
            </div>
            <div class="col-12">
              <label class="form-label">Land</label>
              <select class="form-select" id="customer-country">
                <option value="">Land auswählen</option>
                <option value="Deutschland">Deutschland</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    `,
    currentProgressStep: 0,
    preConfirm: () => {
      const description = document.getElementById('product-description').value.trim()
      const amount = parseFloat(document.getElementById('product-amount').value)
      const customerEmail = document.getElementById('customer-email').value.trim()
      const customerFirstname = document.getElementById('customer-firstname').value.trim()
      const customerLastname = document.getElementById('customer-lastname').value.trim()
      const customerStreet = document.getElementById('customer-street').value.trim()
      const customerNumber = document.getElementById('customer-number').value.trim()
      const customerZip = document.getElementById('customer-zip').value.trim()
      const customerCity = document.getElementById('customer-city').value.trim()
      const customerCountry = document.getElementById('customer-country').value

      if (!description || !amount || amount <= 0) {
        Swal.showValidationMessage(
          'Bitte fülle alle Pflichtfelder aus und gib einen gültigen Betrag ein',
        )
        return false
      }

      const customerName =
        customerFirstname || customerLastname
          ? {
              firstName: customerFirstname,
              lastName: customerLastname,
            }
          : null

      const customerAddress =
        customerStreet || customerZip || customerCity || customerCountry
          ? {
              street: customerStreet,
              number: customerNumber,
              zip: customerZip,
              city: customerCity,
              country: customerCountry,
            }
          : null

      return {
        description,
        amount,
        customerEmail: customerEmail || null,
        customerName,
        customerAddress,
      }
    },
  })

  if (productResult.isDismissed) {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }

  const productData = productResult.value

  const paymentResult = await Queue.fire({
    title: 'Verkaufen - Zahlungsdetails',
    html: `
      <div class="payment-details">
        <h6 class="mb-3">Zahlungsdetails</h6>
        <div class="alert alert-info">
          <div class="row">
            <div class="col-6"><strong>Produkt:</strong></div>
            <div class="col-6 text-end">${productData.description}</div>
          </div>
          <div class="row">
            <div class="col-6"><strong>Betrag:</strong></div>
            <div class="col-6 text-end">${formatCurrency(productData.amount)}</div>
          </div>
          ${
            productData.customerEmail
              ? `
          <div class="row">
            <div class="col-6"><strong>Kunden-E-Mail:</strong></div>
            <div class="col-6 text-end">${productData.customerEmail}</div>
          </div>
          `
              : ''
          }
        </div>
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Zusätzliche Notizen (optional)</label>
            <textarea class="form-control" id="payment-notes" rows="3" placeholder="Zusätzliche Informationen für den Kunden"></textarea>
          </div>
        </div>
      </div>
    `,
    currentProgressStep: 1,
    preConfirm: () => {
      const notes = document.getElementById('payment-notes').value.trim()
      return { notes }
    },
  })

  if (paymentResult.isDismissed) {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }

  const paymentData = paymentResult.value

  const confirmResult = await Queue.fire({
    title: `Verkaufen - Bestätigung ${checkCircleIconHtml}`,
    html: `
      <div class="sell-summary">
        <h6 class="mb-3">Zahlungsanfrage zusammenfassen</h6>
        <div class="alert alert-success">
          <div class="row">
            <div class="col-6"><strong>Produkt:</strong></div>
            <div class="col-6 text-end">${productData.description}</div>
          </div>
          <div class="row">
            <div class="col-6"><strong>Betrag:</strong></div>
            <div class="col-6 text-end"><strong>${formatCurrency(productData.amount)}</strong></div>
          </div>
          ${
            productData.customerEmail
              ? `
          <div class="row">
            <div class="col-6"><strong>Kunden-E-Mail:</strong></div>
            <div class="col-6 text-end">${productData.customerEmail}</div>
          </div>
          `
              : ''
          }
          ${
            productData.customerName
              ? `
          <div class="row">
            <div class="col-6"><strong>Kundenname:</strong></div>
            <div class="col-6 text-end">${productData.customerName.firstName || ''} ${productData.customerName.lastName || ''}</div>
          </div>
          `
              : ''
          }
          ${
            productData.customerAddress
              ? `
          <div class="row">
            <div class="col-6"><strong>Kundenadresse:</strong></div>
            <div class="col-6 text-end">
              ${productData.customerAddress.street || ''} ${productData.customerAddress.number || ''}<br>
              ${productData.customerAddress.zip || ''} ${productData.customerAddress.city || ''}<br>
              ${productData.customerAddress.country || ''}
            </div>
          </div>
          `
              : ''
          }
          ${
            paymentData.notes
              ? `
          <div class="row">
            <div class="col-6"><strong>Notizen:</strong></div>
            <div class="col-6 text-end">${paymentData.notes}</div>
          </div>
          `
              : ''
          }
        </div>
        <div class="mt-3">
          <p class="text-muted small">
            ${creditCardIconHtml} Die Zahlung wird über PayPal abgewickelt. 
            Nach der Erstellung wird die PayPal-Zahlungsseite in einem neuen Fenster geöffnet.
          </p>
        </div>
      </div>
    `,
    currentProgressStep: 2,
    confirmButtonText: 'Zahlungsanfrage erstellen',
    preConfirm: async () => {
      try {
        const { paypalBusinessApi } = await import('@/api/paypalBusiness')

        const returnUrl = `${window.location.origin}/paypal-return?payment=success`
        const cancelUrl = `${window.location.origin}/paypal-return?payment=cancelled`

        const paymentResponse = await paypalBusinessApi.createPayment({
          amount: productData.amount,
          currency: 'EUR',
          description: productData.description,
          returnUrl,
          cancelUrl,
          customerEmail: productData.customerEmail || null,
          customerName: productData.customerName || null,
          customerAddress: productData.customerAddress || null,
        })

        const paymentInfo = {
          paymentId: paymentResponse.data.orderId,
          approvalUrl: paymentResponse.data.approvalUrl,
          amount: productData.amount,
          description: productData.description,
          customerEmail: productData.customerEmail,
          notes: paymentData.notes,
        }

        
        window.open(
          paymentResponse.data.approvalUrl,
          '_blank',
          'width=800,height=600,scrollbars=yes,resizable=yes',
        )

        return paymentInfo
      } catch (error) {
        console.error('PayPal Payment Creation Error:', error)
        Swal.showValidationMessage(
          'Fehler beim Erstellen der Zahlungsanfrage: ' +
            (error.response?.data?.message || error.message),
        )
        return false
      }
    },
    didOpen: (popup) => {
      
      const revocationCheckboxes = popup.querySelectorAll('#revocation-read, #revocation-consent')
      revocationCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          Swal.resetValidationMessage()
        })
      })
    },
  })

  if (confirmResult.isDismissed) {
    if (onCancel) await onCancel()
    return { action: 'cancel' }
  }

  const paymentInfo = confirmResult.value

  if (onComplete) {
    await onComplete({
      paymentId: paymentInfo.paymentId,
      approvalUrl: paymentInfo.approvalUrl,
      amount: paymentInfo.amount,
      description: paymentInfo.description,
      customerEmail: paymentInfo.customerEmail,
      notes: paymentInfo.notes,
    })
  }

  return {
    action: 'complete',
    paymentId: paymentInfo.paymentId,
    approvalUrl: paymentInfo.approvalUrl,
    amount: paymentInfo.amount,
    description: paymentInfo.description,
    customerEmail: paymentInfo.customerEmail,
    notes: paymentInfo.notes,
  }
}
